import React, { useState, useEffect, useCallback } from 'react';
import {
  Button,
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Divider,
  alpha,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  ListItemText,
} from '@mui/material';
import {
  AttachFile as AttachFileIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  Comment as CommentIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  ExpandMore as ExpandMoreIcon,
  InfoOutlined as InfoOutlinedIcon,
  Schedule as ScheduleIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  WarningAmber as WarningAmberIcon,
  LightbulbOutlined as LightbulbOutlinedIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { workflowService, ComplianceStep } from '../services/workflowService';
import { StepCommentsDialog } from './StepCommentsDialog';
import GuidedFormFiller from './GuidedFormFiller';

interface WorkflowStepsPanelProps {
  workflowId: string;
  workflowName: string;
  onOpenEudamedTab?: () => void;
}

export const WorkflowStepsPanel: React.FC<WorkflowStepsPanelProps> = ({
  workflowId,
  workflowName,
  onOpenEudamedTab,
}) => {
  const parseYesNo = useCallback((value: any): boolean | null => {
    if (value === undefined || value === null) return null;
    if (typeof value === 'boolean') return value;
    const raw = `${value}`.trim().toLowerCase();
    if (['yes', 'y', 'true', '1'].includes(raw)) return true;
    if (['no', 'n', 'false', '0'].includes(raw)) return false;
    return null;
  }, []);

  const formatLabel = (label?: string): string => {
    if (!label) return '';
    const titleCase = (word: string) => {
      if (!word) return word;
      if (word !== word.toLowerCase()) return word;
      return `${word.charAt(0).toUpperCase()}${word.slice(1)}`;
    };
    return label
      .split(' ')
      .map(token =>
        token
          .split('/')
          .map(part =>
            part
              .split('-')
              .map(seg => titleCase(seg))
              .join('-')
          )
          .join('/')
      )
      .join(' ');
  };

  const normalizeInfoList = (value: any): string[] => {
    if (!Array.isArray(value)) return [];
    return value
      .map(v => `${v}`.trim())
      .filter(Boolean)
      .filter(v => v.toLowerCase() !== 'none');
  };

  const hasFieldInfo = (field: any): boolean => (
    Boolean(field?.helper_text) ||
    normalizeInfoList(field?.legal_sources).length > 0 ||
    Boolean(field?.why) ||
    normalizeInfoList(field?.rules_triggered).length > 0 ||
    normalizeInfoList(field?.depends_on).length > 0 ||
    normalizeInfoList(field?.influences).length > 0
  );

  const renderFieldInfo = (field: any) => {
    if (!hasFieldInfo(field)) return null;
    const legalSources = normalizeInfoList(field?.legal_sources);
    const rulesTriggered = normalizeInfoList(field?.rules_triggered);
    const dependsOn = normalizeInfoList(field?.depends_on);
    const influences = normalizeInfoList(field?.influences);

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, maxWidth: 520 }}>
        {field.helper_text && (
          <Typography
            component="div"
            variant="caption"
            sx={{ color: '#e5e7eb', lineHeight: 1.35, whiteSpace: 'pre-line' }}
          >
            {field.helper_text}
          </Typography>
        )}
        {legalSources.length > 0 && (
          <Box>
            <Typography component="div" variant="caption" sx={{ color: '#e5e7eb', fontWeight: 700, lineHeight: 1.35 }}>
              Legal Source
            </Typography>
            <Box component="ul" sx={{ pl: 2, m: 0, color: '#e5e7eb', fontSize: '0.75rem' }}>
              {legalSources.map((source: string) => (
                <li key={source}>{source}</li>
              ))}
            </Box>
          </Box>
        )}
        {field.why && (
          <Box>
            <Typography component="div" variant="caption" sx={{ color: '#e5e7eb', fontWeight: 700, lineHeight: 1.35 }}>
              Why This Exists
            </Typography>
            <Typography component="div" variant="caption" sx={{ color: '#e5e7eb', fontSize: '0.75rem', lineHeight: 1.35 }}>
              {field.why}
            </Typography>
          </Box>
        )}
        {rulesTriggered.length > 0 && (
          <Box>
            <Typography component="div" variant="caption" sx={{ color: '#e5e7eb', fontWeight: 700, lineHeight: 1.35 }}>
              Rules It Can Trigger
            </Typography>
            <Box component="ul" sx={{ pl: 2, m: 0, color: '#e5e7eb', fontSize: '0.75rem' }}>
              {rulesTriggered.map((rule: string) => (
                <li key={rule}>{rule}</li>
              ))}
            </Box>
          </Box>
        )}
        {dependsOn.length > 0 && (
          <Box>
            <Typography component="div" variant="caption" sx={{ color: '#e5e7eb', fontWeight: 700, lineHeight: 1.35 }}>
              Depends On
            </Typography>
            <Typography component="div" variant="caption" sx={{ color: '#e5e7eb', fontSize: '0.75rem', lineHeight: 1.35 }}>
              {dependsOn.join(', ')}
            </Typography>
          </Box>
        )}
        {influences.length > 0 && (
          <Box>
            <Typography component="div" variant="caption" sx={{ color: '#e5e7eb', fontWeight: 700, lineHeight: 1.35 }}>
              Influences
            </Typography>
            <Box component="ul" sx={{ pl: 2, m: 0, color: '#e5e7eb', fontSize: '0.75rem' }}>
              {influences.map((item: string) => (
                <li key={item}>{item}</li>
              ))}
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  const renderFieldLabel = (field: any) => {
    const label = formatLabel(field.label);
    if (!hasFieldInfo(field)) return label;
    return (
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
        <span>{label}</span>
        <Tooltip title={renderFieldInfo(field)} placement="top" arrow>
          <IconButton
            size="small"
            onClick={(event) => event.stopPropagation()}
            sx={{ p: 0.25, color: '#9ca3af' }}
          >
            <InfoOutlinedIcon sx={{ fontSize: '0.95rem' }} />
          </IconButton>
        </Tooltip>
      </Box>
    );
  };

  const formatResultLabel = (result: any): string => {
    if (!result) return '';
    const rawLabel = `${result.label || ''}`.trim().toLowerCase();
    const rawKey = `${result.key || ''}`.trim().toLowerCase();
    if (rawKey.includes('conformity_assessment_why') || rawLabel === 'why' || rawLabel.includes('why')) {
      return 'Route Rationale';
    }
    if (rawKey.includes('conformity_assessment_description') || rawLabel === 'description' || rawLabel.includes('description')) {
      return 'Route Description';
    }
    return formatLabel(result.label);
  };

  const toBulletItems = (value: any): string[] => {
    if (Array.isArray(value)) {
      return value.map(v => `${v}`.trim()).filter(Boolean);
    }
    const raw = `${value ?? ''}`.trim();
    if (!raw) return [];
    return raw
      .split(/\r?\n|,|;|\|/g)
      .map(item => item.trim())
      .filter(Boolean);
  };

  const handleDownloadArtifact = async (stepId: string, artifactKey: string, fileName: string) => {
    try {
      const blob = await workflowService.downloadStepArtifact(stepId, artifactKey);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      window.alert('Failed to download file. Please try again.');
    }
  };

  const openCsvEditor = (stepId: string, artifactKey: string, title?: string) => {
    const params = new URLSearchParams();
    params.set('stepId', stepId);
    params.set('artifactKey', artifactKey);
    if (title) params.set('title', title);
    window.open(`/artifacts/editor?${params.toString()}`, '_blank', 'noopener,noreferrer');
  };

  const artifactBtnSx = {
    textTransform: 'none' as const, fontWeight: 600, fontSize: '0.74rem',
    borderRadius: '7px', borderColor: '#d1d5db', color: '#374151', px: 1.25, py: 0.25,
    '&:hover': { borderColor: '#9ca3af', bgcolor: '#f9fafb' },
  };

  const renderArtifactDownload = (stepId: string, resultKey: string, value: any, defaultFileName: string) => {
    const isBusy = savingInputsStepId === stepId || resultLoadingStepId === stepId || editingInputsStepId === stepId;
    const options = Array.isArray(value) ? value : [];
    const hasOptions = options.length > 0 && typeof options[0] === 'object' && Boolean((options[0] as any)?.artifact_key);
    if (!hasOptions) {
      return (
        <Box sx={{ mt: 0.5 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<DownloadIcon sx={{ fontSize: '0.85rem !important' }} />}
            onClick={() => handleDownloadArtifact(stepId, resultKey, defaultFileName)}
            disabled={isBusy}
            sx={artifactBtnSx}
          >
            Download CSV
          </Button>
        </Box>
      );
    }

    const selectionKey = `${stepId}:${resultKey}`;
    const selectedKey = selectedArtifactKeyByStepAndResultKey[selectionKey] || (options[0] as any).artifact_key;
    const selected = options.find((opt: any) => opt?.artifact_key === selectedKey) || options[0];
    const fileName = (selected as any)?.filename || defaultFileName;
    const canEdit = `${selectedKey || ''}`.toLowerCase().endsWith('_csv');
    const editorTitle = `${selected?.label || selected?.standard || fileName || 'Sheet'}`.trim();

    return (
      <Box sx={{ mt: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel sx={{ fontSize: '0.78rem' }}>Standard</InputLabel>
            <Select
              value={selectedKey}
              label="Standard"
              onChange={(e) => setSelectedArtifactKeyByStepAndResultKey(prev => ({ ...prev, [selectionKey]: `${e.target.value}` }))}
              disabled={isBusy}
              sx={{
                fontSize: '0.78rem', borderRadius: '7px',
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d1d5db' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#9ca3af' },
                '& .MuiSelect-select': { py: '6px' },
              }}
            >
              {options.map((opt: any) => (
                <MenuItem key={opt.artifact_key} value={opt.artifact_key} sx={{ fontSize: '0.78rem' }}>
                  {opt.label || opt.standard || opt.artifact_key}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            size="small"
            variant="outlined"
            startIcon={<DownloadIcon sx={{ fontSize: '0.85rem !important' }} />}
            onClick={() => handleDownloadArtifact(stepId, selectedKey, fileName)}
            disabled={isBusy}
            sx={artifactBtnSx}
          >
            Download CSV
          </Button>
          {canEdit && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<EditIcon sx={{ fontSize: '0.85rem !important' }} />}
              onClick={() => openCsvEditor(stepId, `${selectedKey}`, editorTitle)}
              disabled={isBusy}
              sx={artifactBtnSx}
            >
              Edit
            </Button>
          )}
        </Box>
      </Box>
    );
  };

  const renderResultValue = (stepId: string, resultKey: string, value: any) => {
    if (resultKey === 'standards_selected' && Array.isArray(value) && value.length > 0) {
      const isRichFormat = typeof value[0] === 'object' && value[0]?.code;
      const items: Array<{ code: string; justification: string }> = isRichFormat
        ? value
        : value.map((v: any) => ({ code: String(v), justification: '' }));

      const hasAnyJustification = items.some((i) => !!i.justification);
      return (
        <Box sx={{ mt: 0.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {items.map((item) =>
            item.justification ? (
              <Accordion
                key={item.code}
                defaultExpanded
                disableGutters
                elevation={0}
                sx={{
                  border: '1px solid #eceef1',
                  borderRadius: '8px !important',
                  '&:before': { display: 'none' },
                  '&.Mui-expanded': { margin: 0 },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ fontSize: 16, color: '#9ca3af' }} />}
                  sx={{
                    minHeight: 0,
                    px: 1.5,
                    py: 0.5,
                    '& .MuiAccordionSummary-content': { margin: '6px 0', alignItems: 'center', gap: 1 },
                    '&.Mui-expanded': { minHeight: 0 },
                  }}
                >
                  <CheckCircleOutlineIcon sx={{ fontSize: 15, color: '#16a34a', flexShrink: 0 }} />
                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#111827' }}>
                    {item.code}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 1.5, pt: 0, pb: 1 }}>
                  <Typography sx={{ fontSize: '0.75rem', color: '#6b7280', lineHeight: 1.5, pl: 3 }}>
                    {item.justification}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ) : (
              <Box
                key={item.code}
                sx={{
                  border: '1px solid #eceef1',
                  borderRadius: '8px',
                  px: 1.5,
                  py: 0.75,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <CheckCircleOutlineIcon sx={{ fontSize: 15, color: '#16a34a', flexShrink: 0 }} />
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#111827' }}>
                  {item.code}
                </Typography>
              </Box>
            )
          )}
          <Typography sx={{ fontSize: '0.7rem', color: '#9ca3af', mt: 0.5 }}>
            {items.length} standard{items.length !== 1 ? 's' : ''} applicable
            {!hasAnyJustification && ' — regenerate to see justifications'}
          </Typography>
        </Box>
      );
    }
    if (resultKey === 'standards_mapping_csv') {
      return renderArtifactDownload(stepId, resultKey, value, 'standards-mapping.csv');
    }
    if (resultKey === 'test_vs_justification_csv') {
      return renderArtifactDownload(stepId, resultKey, value, 'test-vs-justification.csv');
    }
    if (resultKey === 'evidence_mapping_workbook_xlsx') {
      const isBusy = savingInputsStepId === stepId || resultLoadingStepId === stepId || editingInputsStepId === stepId;
      return (
        <Box sx={{ mt: 0.5 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<DownloadIcon sx={{ fontSize: '0.85rem !important' }} />}
            onClick={() => handleDownloadArtifact(stepId, 'evidence_mapping_workbook_xlsx', 'evidence-mapping-workbook.xlsx')}
            disabled={isBusy}
            sx={artifactBtnSx}
          >
            Download XLSX
          </Button>
        </Box>
      );
    }

    // Generic: if a result value is an artifact list (array of {artifact_key,...}),
    // show the standard dropdown + download button even if we haven't added a bespoke renderer yet.
    if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object' && Boolean((value[0] as any)?.artifact_key)) {
      const safeBase = `${resultKey || 'artifact'}`.replace(/_csv$/i, '').replace(/[^a-z0-9]+/gi, '-').replace(/^-+|-+$/g, '').toLowerCase();
      const fallbackName = `${safeBase || 'artifact'}.csv`;
      return renderArtifactDownload(stepId, `${resultKey}`, value, fallbackName);
    }
    if (resultKey === 'result_sensitivity_flags') {
      const items = toBulletItems(value).filter(v => v.toLowerCase() !== 'none identified');
      if (items.length === 0) {
        return <Typography variant="body2" sx={{ color: '#111827', mt: 0.5 }}>None Identified</Typography>;
      }
      return (
        <Box component="ul" sx={{ pl: 2, mt: 0.75, mb: 0, color: '#111827' }}>
          {items.map(item => (
            <li key={item}>
              <Typography variant="body2" sx={{ color: '#111827' }}>
                {item}
              </Typography>
            </li>
          ))}
        </Box>
      );
    }

    if (Array.isArray(value)) {
      const items = toBulletItems(value);
      return (
        <Box component="ul" sx={{ pl: 2, mt: 0.75, mb: 0, color: '#111827' }}>
          {items.map(item => (
            <li key={item}>
              <Typography variant="body2" sx={{ color: '#111827' }}>
                {item}
              </Typography>
            </li>
          ))}
        </Box>
      );
    }

    return (
      <Typography variant="body2" sx={{ color: '#111827', mt: 0.5 }}>
        {value || 'Not generated yet'}
      </Typography>
    );
  };

  const renderStepInfoIcon = (stepTitle: string, stepDescription?: string) => {
    const key = (stepTitle || '').toLowerCase();
    const tooltipTextMap: Record<string, string> = {
      classification:
        'Determines the MDR risk class (I, IIa, IIb, III) based on device characteristics such as invasiveness, duration of use, implantability, and software function. This classification drives all downstream regulatory requirements.',
      'regulatory pathway':
        'Identifies the required conformity assessment route (e.g., Annex IX, X, XI), whether a Notified Body is needed, and which economic operators must be involved before CE marking.',
      'evidence mapping':
        'Maps required clinical, technical, and risk documentation to MDR Annex I, II, and III. Highlights what evidence is mandatory based on device class and intended use.',
      'submission guidance':
        'Generates a complete submission roadmap with step-by-step timeline, EUDAMED module mapping, Notified Body deliverables, technical documentation checklist, blocking dependencies, and common pitfalls specific to your device class and conformity route.',
      'document workspace':
        'Central repository to upload, organize, and track required regulatory documents such as technical documentation, clinical evaluation reports, risk management files, and QMS records.',
    };

    const tooltipText = tooltipTextMap[key] || stepDescription || 'Step details and guidance.';
    const isClassification = key === 'classification';

    return (
      <Tooltip
        title={
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, maxWidth: 520 }}>
            <Typography variant="caption" sx={{ color: '#e5e7eb', fontWeight: 600 }}>
              {formatLabel(stepTitle)}
            </Typography>
            <Typography variant="caption" sx={{ color: '#e5e7eb', fontSize: '0.75rem' }}>
              {tooltipText}
            </Typography>
          </Box>
        }
        placement="top"
        arrow
      >
        <IconButton
          size="small"
          onClick={(event) => {
            event.stopPropagation();
            if (isClassification) {
              window.open('/mdr-annex-rules', '_blank', 'noopener,noreferrer');
            }
          }}
          sx={{ color: '#6b7280' }}
        >
          <InfoOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    );
  };

  const computeNotifiedBodyValue = useCallback((
    deviceClassValue: string,
    classificationValues: Record<string, any>
  ): string => {
    const rawClass = `${deviceClassValue || ''}`.trim();
    if (!rawClass) {
      return 'Unknown — classification incomplete';
    }
    const normalized = rawClass
      .toLowerCase()
      .replace('class', '')
      .replace(/[\s-]/g, '');

    const impliedSterile = normalized === 'is';
    const impliedMeasuring = normalized === 'im';
    const impliedReusable = normalized === 'ir';

    const sterile = parseYesNo(classificationValues.is_sterile);
    const measuring = parseYesNo(classificationValues.has_measuring_function);
    const surgicalInstrument = parseYesNo(classificationValues.is_surgical_instrument);
    const reusable = parseYesNo(classificationValues.is_reusable_surgical);

    const resolvedSterile = sterile ?? (impliedSterile ? true : null);
    const resolvedMeasuring = measuring ?? (impliedMeasuring ? true : null);
    const resolvedSurgicalInstrument = surgicalInstrument ?? (impliedReusable ? true : null);
    const resolvedReusable = reusable
      ?? (impliedReusable ? true : resolvedSurgicalInstrument === false ? false : null);

    const classIsI = ['i', 'is', 'im', 'ir'].includes(normalized);
    const classIsHigher = ['iia', 'iib', 'iii'].includes(normalized);
    const deviceType = `${classificationValues.device_type || 'standard'}`.trim().toLowerCase();

    if (deviceType === 'custom_made') {
      return 'Notified Body not required';
    }

    if (deviceType === 'investigational') {
      return 'Possibly';
    }

    if (deviceType === 'system_procedure_pack') {
      return 'Depends';
    }

    if (classIsHigher) {
      return 'Notified Body required';
    }

    if (classIsI) {
      const missing: string[] = [];
      if (resolvedSterile === null) missing.push('sterility');
      if (resolvedMeasuring === null) missing.push('measuring function');
      if (resolvedSurgicalInstrument === true && resolvedReusable === null) {
        missing.push('reusable surgical instrument');
      } else if (resolvedSurgicalInstrument === null && resolvedReusable === null) {
        missing.push('surgical instrument use');
      }
      if (missing.length > 0) {
        return `Unknown — missing: ${missing.join(', ')}`;
      }
      if (resolvedSterile || resolvedMeasuring || resolvedReusable) {
        return 'Notified Body required';
      }
      return 'Self-certification';
    }

    return 'Unknown — classification incomplete';
  }, [parseYesNo]);

  const deriveImplantableValue = useCallback((classificationValues: Record<string, any>) => {
    const explicit = parseYesNo(classificationValues?.implantable);
    if (explicit !== null) return explicit ? 'Yes' : 'No';

    const bodyContact = `${classificationValues?.body_contact_level ?? ''}`.trim().toLowerCase();
    if (!bodyContact) return undefined;
    if (bodyContact.includes('implanted')) return 'Yes';
    return 'No';
  }, [parseYesNo]);
  const [steps, setSteps] = useState<ComplianceStep[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedStepId, setSelectedStepId] = useState<string | null>(null);
  const [selectedStepTitle, setSelectedStepTitle] = useState('');
  const [selectedStepComments, setSelectedStepComments] = useState('');
  const [stepStatusChanges, setStepStatusChanges] = useState<Record<string, string>>({});
  const [editingStepId, setEditingStepId] = useState<string | null>(null);
  const [editingFields, setEditingFields] = useState<{
    title?: string;
    due_date?: string;
    estimated_days?: number;
  }>({});
  const [showAddStepDialog, setShowAddStepDialog] = useState(false);
  const [newStepData, setNewStepData] = useState({
    title: '',
    description: '',
    category: 'general',
    estimated_days: 30,
  });
  const [deletingStepId, setDeletingStepId] = useState<string | null>(null);
  const [showFormFiller, setShowFormFiller] = useState(false);
  const [formFillerStep, setFormFillerStep] = useState<ComplianceStep | null>(null);
  const [dataFieldValues, setDataFieldValues] = useState<Record<string, Record<string, any>>>({});
  const [expandedStepId, setExpandedStepId] = useState<string | null>(null);
  const [resultLoadingStepId, setResultLoadingStepId] = useState<string | null>(null);
  const [savingInputsStepId, setSavingInputsStepId] = useState<string | null>(null);
  const [editingInputsStepId, setEditingInputsStepId] = useState<string | null>(null);
  const hasInitializedExpansion = React.useRef(false);
  const [workflowData, setWorkflowData] = useState<{
    jurisdiction?: string;
    device_class?: string;
    device_type?: string;
    device_category?: string;
    created_at?: string | null;
  } | null>(null);
  const [workflowInputs, setWorkflowInputs] = useState<Array<{ key: string; label: string; value: string }>>([]);
  const [workflowInputsMode, setWorkflowInputsMode] = useState<'edit' | 'view'>('edit');
  const [selectedArtifactKeyByStepAndResultKey, setSelectedArtifactKeyByStepAndResultKey] = useState<Record<string, string>>({});
  const useNewWorkflowUI = true;

  const fetchWorkflowStatus = useCallback(async (options?: { silent?: boolean }) => {
    if (!workflowId) {
      console.error('No workflowId provided to fetchWorkflowStatus');
      setError('No workflow ID provided. Please try again.');
      return;
    }

    if (!options?.silent) {
      setIsLoading(true);
    }
    setError(null);

    try {
      console.log('Fetching workflow status for ID:', workflowId);
      const response = await workflowService.getWorkflowStatus(workflowId);
      console.log('Workflow status response:', response);
      setSteps(response.steps);
      // Store workflow data for form filler
      if (response.workflow) {
        setWorkflowData({
          jurisdiction: response.workflow.jurisdiction,
          device_class: response.workflow.device_class,
          device_type: response.workflow.device_type,
          device_category: response.workflow.device_category,
          created_at: response.workflow.created_at || null,
        });
        const inputs = (response.workflow.configuration as any)?.workflow_inputs;
        if (Array.isArray(inputs)) {
          setWorkflowInputs(inputs.map((input: any) => ({
            key: input.key,
            label: input.label || input.key,
            value: input.value ?? '',
          })));
        } else {
          setWorkflowInputs([]);
        }
      }

      // Refresh data field values from latest step configuration
      if (Array.isArray(response.steps)) {
        setDataFieldValues(prev => {
          const next = { ...prev };
          const classificationStep = response.steps.find(
            step => step.title?.toLowerCase() === 'classification'
          );
          const classificationValues =
            (classificationStep?.configuration as any)?.data_values || {};
          const classificationClass =
            classificationValues.result_class || classificationValues.class || '';
          const derivedImplantable = deriveImplantableValue(classificationValues);
          response.steps.forEach(step => {
            const existingValues = (step.configuration as any)?.data_values || {};
            if (step.title?.toLowerCase() === 'regulatory pathway') {
              const computedValue = existingValues.notified_body_involvement
                ? existingValues.notified_body_involvement
                : computeNotifiedBodyValue(`${classificationClass}`, classificationValues);
              next[step.id] = {
                ...existingValues,
                notified_body_involvement: computedValue,
                is_sterile: classificationValues.is_sterile ?? existingValues.is_sterile,
                has_measuring_function: classificationValues.has_measuring_function ?? existingValues.has_measuring_function,
                is_surgical_instrument: classificationValues.is_surgical_instrument ?? existingValues.is_surgical_instrument,
                is_reusable_surgical: classificationValues.is_reusable_surgical ?? existingValues.is_reusable_surgical,
                implantable: derivedImplantable ?? existingValues.implantable,
                manufacturer_in_eu: classificationValues.manufacturer_in_eu ?? existingValues.manufacturer_in_eu,
              };
            } else {
              next[step.id] = { ...existingValues };
            }
          });
          return next;
        });
      }
    } catch (err) {
      console.error('Error fetching workflow status:', err);

      // Check if it's an authentication error
      if (err instanceof Error && err.message.includes('401')) {
        setError('Authentication required. Please log in to view workflow steps.');
      } else if (err instanceof Error && err.message.includes('403')) {
        setError('Access denied. You do not have permission to view this workflow.');
      } else {
        setError(`Failed to load workflow steps. Please try again. Error: ${err instanceof Error ? err.message : 'Unknown error'}`);
      }
    } finally {
      if (!options?.silent) {
        setIsLoading(false);
      }
    }
  }, [workflowId, computeNotifiedBodyValue, deriveImplantableValue]);

  useEffect(() => {
    if (workflowId) {
      fetchWorkflowStatus();
    } else {
      setSteps([]);
      setError(null);
      setSelectedStepId(null);
      setStepStatusChanges({});
    }
  }, [workflowId, fetchWorkflowStatus]);

  useEffect(() => {
    if (!steps.length) {
      setExpandedStepId(null);
      hasInitializedExpansion.current = false;
      return;
    }
    if (!hasInitializedExpansion.current) {
      setExpandedStepId(steps[0]?.id || null);
      hasInitializedExpansion.current = true;
    } else if (expandedStepId && !steps.some(step => step.id === expandedStepId)) {
      setExpandedStepId(steps[0]?.id || null);
    }
  }, [steps, expandedStepId]);

  useEffect(() => {
    if (!steps.length) return;
    setDataFieldValues(prev => {
      const next = { ...prev };
      steps.forEach(step => {
        if (!next[step.id]) {
          const existingValues = (step.configuration as any)?.data_values || {};
          next[step.id] = { ...existingValues };
        }
      });
      return next;
    });
  }, [steps]);

  const handleCommentsClick = (step: ComplianceStep) => {
    setSelectedStepId(step.id);
    setSelectedStepTitle(step.title);
    setSelectedStepComments(step.comments || '');
  };

  const handleStatusChange = (stepId: string, newStatus: string) => {
    setStepStatusChanges(prev => ({ ...prev, [stepId]: newStatus }));
  };

  const handleUpdateStepStatus = async (stepId: string, newStatus: string) => {
    try {
      await workflowService.updateStepStatus(stepId, newStatus);
      setStepStatusChanges(prev => {
        const updated = { ...prev };
        delete updated[stepId];
        return updated;
      });
      await fetchWorkflowStatus();
    } catch (error) {
      window.alert('Failed to update step status. Please try again.');
    }
  };

  const handleStartStep = async (stepId: string) => {
    try {
      await workflowService.startStep(stepId);
      setStepStatusChanges(prev => {
        const updated = { ...prev };
        delete updated[stepId];
        return updated;
      });
      await fetchWorkflowStatus();
    } catch (error) {
      window.alert('Failed to start step. Please try again.');
    }
  };

  const handleCompleteStep = async (stepId: string) => {
    try {
      await workflowService.completeStep(stepId);
      setStepStatusChanges(prev => {
        const updated = { ...prev };
        delete updated[stepId];
        return updated;
      });
      await fetchWorkflowStatus();
    } catch (error) {
      window.alert('Failed to complete step. Please try again.');
    }
  };

  const handleRunStep = async (stepId: string) => {
    try {
      setResultLoadingStepId(stepId);
      await workflowService.runStep(stepId);
      setStepStatusChanges(prev => {
        const updated = { ...prev };
        delete updated[stepId];
        return updated;
      });
      await fetchWorkflowStatus({ silent: true });
    } catch (error) {
      window.alert('Failed to run step. Please try again.');
    } finally {
      setResultLoadingStepId(null);
    }
  };

  const isStepReadyForRun = (step: ComplianceStep): boolean => {
    const fields = (step.configuration as any)?.data_fields || [];
    if (!Array.isArray(fields) || fields.length === 0) {
      return true;
    }
    const values = dataFieldValues[step.id] || {};
    return fields.every((field: any) => {
      if (!field.required) return true;
      const value = values[field.key];
      return typeof value === 'string' ? value.trim().length > 0 : Boolean(value);
    });
  };

  const hasMeaningfulValue = (value: any): boolean => {
    if (value === undefined || value === null) return false;
    if (typeof value === 'string') return value.trim().length > 0;
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === 'object') return Object.keys(value).length > 0;
    return Boolean(value);
  };

  const stepHasFormData = (step: ComplianceStep, fieldsOverride?: any[]): boolean => {
    const config = (step.configuration || {}) as any;
    const fields = Array.isArray(fieldsOverride) ? fieldsOverride : (Array.isArray(config.fields) ? config.fields : []);
    if (fields.length === 0) return false;
    const values = dataFieldValues[step.id] || config.data_values || {};
    return fields.some((field: any) => hasMeaningfulValue(values[field.key]));
  };

  const stepHasGeneratedOutputs = (step: ComplianceStep, resultsOverride?: any[]): boolean => {
    const config = (step.configuration || {}) as any;
    const results = Array.isArray(resultsOverride) ? resultsOverride : (Array.isArray(config.results) ? config.results : []);
    const values = dataFieldValues[step.id] || config.data_values || {};
    const hasResultValues = results.some((result: any) => hasMeaningfulValue(values[result.key]));
    if (hasResultValues) return true;
    const artifacts = config.artifacts;
    return Boolean(artifacts && typeof artifacts === 'object' && Object.keys(artifacts).length > 0);
  };

  const handleSkipStep = async (stepId: string) => {
    try {
      await workflowService.skipStep(stepId);
      setStepStatusChanges(prev => {
        const updated = { ...prev };
        delete updated[stepId];
        return updated;
      });
      await fetchWorkflowStatus();
    } catch (error) {
      window.alert('Failed to skip step. Please try again.');
    }
  };

  const getStatusConfig = (status: string) => {
    const configs: Record<string, { label: string; color: string; bgColor: string }> = {
      pending: { label: 'PENDING', color: '#6b7280', bgColor: '#f3f4f6' },
      in_progress: { label: 'IN PROGRESS', color: '#3b82f6', bgColor: '#dbeafe' },
      completed: { label: 'COMPLETED', color: '#10b981', bgColor: '#d1fae5' },
      skipped: { label: 'SKIPPED', color: '#9ca3af', bgColor: '#f9fafb' },
      blocked: { label: 'BLOCKED', color: '#ef4444', bgColor: '#fee2e2' },
    };
    return configs[status] || configs.pending;
  };

  const getTaskStatuses = (step: ComplianceStep, tasks: string[]): boolean[] => {
    const rawStatuses = step.configuration?.task_statuses;
    if (Array.isArray(rawStatuses)) {
      return tasks.map((_, idx) => Boolean(rawStatuses[idx]));
    }
    return tasks.map(() => false);
  };

  const handleToggleTask = async (
    step: ComplianceStep,
    tasks: string[],
    taskIndex: number
  ) => {
    try {
      const currentStatuses = getTaskStatuses(step, tasks);
      const nextStatuses = currentStatuses.map((value, idx) =>
        idx === taskIndex ? !value : value
      );
      const completedCount = nextStatuses.filter(Boolean).length;
      const nextStatus =
        completedCount === 0
          ? 'pending'
          : completedCount === tasks.length
            ? 'completed'
            : 'in_progress';

      await workflowService.updateStepDetails(step.id, {
        configuration: {
          ...(step.configuration || {}),
          task_statuses: nextStatuses,
        },
      });

      if (nextStatus !== step.status) {
        await workflowService.updateStepStatus(step.id, nextStatus);
      }

      setSteps(prev =>
        prev.map(s =>
          s.id === step.id
            ? {
                ...s,
                status: nextStatus as ComplianceStep['status'],
                configuration: {
                  ...(s.configuration || {}),
                  task_statuses: nextStatuses,
                },
              }
            : s
        )
      );
    } catch (error) {
      window.alert('Failed to update task status. Please try again.');
    }
  };

  const getAvailableStatusOptions = (currentStatus: string): string[] => {
    const allStatuses = ['pending', 'in_progress', 'completed', 'skipped', 'blocked'];
    return allStatuses.filter(s => s !== currentStatus);
  };

  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return 'Not set';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return 'Invalid date';
    }
  };

  const formatDateForInput = (dateString: string | null | undefined): string => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toISOString().split('T')[0]; // YYYY-MM-DD format
    } catch {
      return '';
    }
  };

  const handleStartEdit = (step: ComplianceStep) => {
    setEditingStepId(step.id);
    setEditingFields({
      title: step.title,
      due_date: formatDateForInput(step.due_date),
      estimated_days: step.estimated_days,
    });
  };

  const handleCancelEdit = () => {
    setEditingStepId(null);
    setEditingFields({});
  };

  const handleSaveEdit = async (stepId: string) => {
    try {
      const updates: any = {};
      if (editingFields.title !== undefined) updates.title = editingFields.title;
      if (editingFields.due_date !== undefined) {
        if (editingFields.due_date) {
          // Convert to ISO format with time
          const date = new Date(editingFields.due_date);
          updates.due_date = date.toISOString();
        } else {
          updates.due_date = null;
        }
      }
      if (editingFields.estimated_days !== undefined) updates.estimated_days = editingFields.estimated_days;

      await workflowService.updateStepDetails(stepId, updates);
      setEditingStepId(null);
      setEditingFields({});
      await fetchWorkflowStatus();
    } catch (error) {
      window.alert('Failed to update step. Please try again.');
    }
  };

  const handleDataFieldChange = (stepId: string, key: string, value: any) => {
    setDataFieldValues(prev => ({
      ...prev,
      [stepId]: {
        ...(prev[stepId] || {}),
        [key]: value,
      },
    }));
  };

  const handleSaveDataFields = async (stepId: string) => {
    try {
      setSavingInputsStepId(stepId);
      const values = { ...(dataFieldValues[stepId] || {}) };
      const step = steps.find(item => item.id === stepId);
      if (step?.title?.toLowerCase() === 'regulatory pathway') {
        const classificationStep = steps.find(
          item => item.title?.toLowerCase() === 'classification'
        );
        const classificationValues =
          (classificationStep?.configuration as any)?.data_values || {};
        const classificationClass =
          classificationValues.result_class || classificationValues.class || '';
        values.notified_body_involvement = computeNotifiedBodyValue(
          `${classificationClass}`,
          classificationValues
        );
        values.is_sterile = classificationValues.is_sterile ?? values.is_sterile;
        values.has_measuring_function = classificationValues.has_measuring_function ?? values.has_measuring_function;
        values.is_surgical_instrument = classificationValues.is_surgical_instrument ?? values.is_surgical_instrument;
        values.is_reusable_surgical = classificationValues.is_reusable_surgical ?? values.is_reusable_surgical;
        values.implantable = deriveImplantableValue(classificationValues) ?? values.implantable;
        values.manufacturer_in_eu = classificationValues.manufacturer_in_eu ?? values.manufacturer_in_eu;
      }
      await workflowService.updateStepDetails(stepId, {
        configuration: { data_values: values },
      });
      await fetchWorkflowStatus();
    } catch (error) {
      window.alert('Failed to save inputs. Please try again.');
    } finally {
      setSavingInputsStepId(null);
    }
  };

  const handleCancelInputs = (step: ComplianceStep) => {
    const existingValues = (step.configuration as any)?.data_values || {};
    setDataFieldValues(prev => ({
      ...prev,
      [step.id]: { ...existingValues },
    }));
    setEditingInputsStepId(null);
  };

  const handleWorkflowInputChange = (index: number, value: string) => {
    if (workflowInputsMode === 'view') {
      setWorkflowInputsMode('edit');
    }
    setWorkflowInputs(prev => prev.map((item, idx) => (
      idx === index ? { ...item, value } : item
    )));
  };

  const handleSaveWorkflowInputs = async () => {
    try {
      await workflowService.updateWorkflowDetails(workflowId, {
        configuration: { workflow_inputs: workflowInputs },
      });
      setWorkflowInputsMode('view');
      await fetchWorkflowStatus();
    } catch (error) {
      window.alert('Failed to save workflow inputs. Please try again.');
    }
  };

  const handleAddStep = async () => {
    if (!newStepData.title.trim()) {
      window.alert('Please enter a step title');
      return;
    }

    try {
      await workflowService.createStep(workflowId, newStepData);
      setShowAddStepDialog(false);
      setNewStepData({
        title: '',
        description: '',
        category: 'general',
        estimated_days: 30,
      });
      await fetchWorkflowStatus();
    } catch (error) {
      window.alert('Failed to create step. Please try again.');
    }
  };

  const handleDeleteStep = async (stepId: string) => {
    if (!window.confirm('Are you sure you want to delete this step? This action cannot be undone.')) {
      return;
    }

    try {
      setDeletingStepId(stepId);
      await workflowService.deleteStep(stepId);
      await fetchWorkflowStatus();
    } catch (error) {
      window.alert('Failed to delete step. Please try again.');
    } finally {
      setDeletingStepId(null);
    }
  };

  if (!workflowId) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Select a workflow to view its steps
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ pt: 0.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 2 }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            title={workflowName}
            sx={{
              fontWeight: 700,
              fontSize: '1.1rem',
              color: '#111827',
              letterSpacing: '-0.01em',
              mb: 0.25,
              whiteSpace: 'normal',
              overflowWrap: 'anywhere',
              wordBreak: 'break-word',
            }}
          >
            {workflowName}
          </Typography>
          {workflowData?.created_at && (
            <Typography sx={{ color: '#9ca3af', fontSize: '0.78rem' }}>
              Created {new Date(workflowData.created_at).toLocaleString()}
            </Typography>
          )}
        </Box>
        {/* Quick Docs summary */}
        {(() => {
          const docCount = steps.reduce((acc, s) => {
            const cfg: any = s.configuration || {};
            const arts = cfg.artifacts;
            if (arts && typeof arts === 'object') {
              acc += Object.keys(arts).filter(k => k.endsWith('_csv') || k.endsWith('_xlsx')).length;
            }
            return acc;
          }, 0);
          if (docCount === 0) return null;
          return (
            <Box
              sx={{
                border: '1px solid #eceef1',
                borderRadius: '10px',
                px: 1.5,
                py: 0.75,
                bgcolor: '#fafbfc',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                cursor: 'pointer',
                '&:hover': { bgcolor: '#f3f4f6', borderColor: '#d1d5db' },
                transition: 'all 0.15s ease',
              }}
              onClick={() => {
                const docStep = steps.find(s => (s.title || '').toLowerCase() === 'document workspace');
                if (docStep) setExpandedStepId(docStep.id);
              }}
            >
              <DownloadIcon sx={{ fontSize: 16, color: '#6b7280' }} />
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#374151', whiteSpace: 'nowrap' }}>
                {docCount} Doc{docCount !== 1 ? 's' : ''}
              </Typography>
            </Box>
          );
        })()}
      </Box>

      {/* Content */}
      <Box>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 1.5, borderRadius: 1.5 }}>
            {error}
          </Alert>
        ) : (
          useNewWorkflowUI ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              {steps.map((step) => {
                const config = (step.configuration || {}) as any;
                const tasks: string[] = Array.isArray(config.tasks) ? config.tasks : [];
                const fields: any[] = Array.isArray(config.fields) ? config.fields : [];
                const filteredFields = fields.filter((field: any) => field.key !== 'eudamed_similar_devices');
                const results: any[] = Array.isArray(config.results) ? config.results : [];
                const outputs: string[] = Array.isArray(config.outputs) ? config.outputs : [];
                const links: any[] = Array.isArray(config.links) ? config.links : [];
                const successCriteria = config.success_criteria;
                const taskStatuses = getTaskStatuses(step, tasks);
                const visibleFields = filteredFields.filter((field: any) => {
                  const showIf = field.show_if;
                  if (!showIf) return true;
                  const current = dataFieldValues[step.id]?.[showIf.key];
                  return showIf.values?.includes(current);
                });
                const hasEnteredFormData = stepHasFormData(step, visibleFields);
                const hasGeneratedOutputs = stepHasGeneratedOutputs(step, results);
                const isFieldBased = visibleFields.length > 0;
                const isResultsOnly = !isFieldBased && tasks.length === 0 && results.length > 0;
                const completedCount = isFieldBased
                  ? visibleFields.filter((field: any) => {
                      const value = dataFieldValues[step.id]?.[field.key];
                      if (Array.isArray(value)) return value.length > 0;
                      return value !== undefined && value !== null && `${value}`.trim() !== '';
                    }).length
                  : isResultsOnly
                    ? results.filter((r: any) => {
                        const v = dataFieldValues[step.id]?.[r.key];
                        if (Array.isArray(v)) return v.length > 0;
                        return v !== undefined && v !== null && `${v}`.trim() !== '';
                      }).length
                    : taskStatuses.filter(Boolean).length;
                const totalCount = isFieldBased ? visibleFields.length : isResultsOnly ? results.length : tasks.length;
                const statusConfig = getStatusConfig(step.status);
                const isDocumentWorkspace = (step.title || '').toLowerCase() === 'document workspace';
                return (
                  <Accordion
                    key={step.id}
                    expanded={expandedStepId === step.id}
                    onChange={(_, isExpanded) => setExpandedStepId(isExpanded ? step.id : null)}
                    sx={{
                      border: '1px solid #eceef1',
                      borderRadius: '12px',
                      boxShadow: '0 1px 3px rgba(16, 24, 40, 0.04)',
                      bgcolor: '#fff',
                      '&:before': { display: 'none' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon sx={{ fontSize: '1.1rem', color: '#9ca3af' }} />}
                      sx={{
                        px: 2,
                        minHeight: 52,
                        borderBottom: '1px solid #f0f1f3',
                        '& .MuiAccordionSummary-content': { my: 0.75 },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <Typography sx={{ fontWeight: 650, fontSize: '0.9rem', color: '#111827', letterSpacing: '-0.01em' }}>
                            {step.title}
                          </Typography>
                          {renderStepInfoIcon(step.title, (step as any).description)}
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                          {!(isResultsOnly && completedCount === 0) && totalCount > 0 && (
                            <Typography sx={{ fontWeight: 600, fontSize: '0.78rem', color: '#9ca3af' }}>
                              {completedCount}/{totalCount}
                            </Typography>
                          )}
                          <Box
                            sx={{
                              px: 0.9,
                              py: 0.2,
                              borderRadius: 999,
                              fontSize: '0.64rem',
                              fontWeight: 700,
                              color: statusConfig.color,
                              bgcolor: statusConfig.bgColor,
                              textTransform: 'uppercase',
                              letterSpacing: '0.04em',
                            }}
                          >
                            {statusConfig.label}
                          </Box>
                        </Box>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: 2, pt: 0, pb: 2 }}>
                      {successCriteria && (
                        <>
                          <Typography sx={{ mb: 1, color: '#6b7280', fontSize: '0.82rem', fontWeight: 500, lineHeight: 1.5 }}>
                            {successCriteria}
                          </Typography>
                          <Divider sx={{ mb: 1 }} />
                        </>
                      )}
                      {filteredFields.length > 0 && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: tasks.length > 0 ? 1.25 : 0 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography sx={{ fontWeight: 650, fontSize: '0.82rem', color: '#111827' }}>
                              Inputs
                            </Typography>
                            {editingInputsStepId !== step.id && (
                              <IconButton
                                size="small"
                                onClick={() => setEditingInputsStepId(step.id)}
                                sx={{ color: '#9ca3af', '&:hover': { color: '#374151', bgcolor: '#f3f4f6' } }}
                              >
                                <EditIcon sx={{ fontSize: '0.9rem' }} />
                              </IconButton>
                            )}
                          </Box>
                          {(() => {
                            const isEditing = editingInputsStepId === step.id;
                            const visible = visibleFields;
                            const groups: Array<{ key: string; label: string; fields: any[] }> = [];
                            const byKey = new Map<string, { key: string; label: string; fields: any[] }>();
                            visible.forEach((field: any) => {
                              const gKey = `${field.group || 'other'}`;
                              const gLabel = `${field.group_label || field.group || 'Other'}`;
                              const existing = byKey.get(gKey);
                              if (existing) {
                                existing.fields.push(field);
                                return;
                              }
                              const entry = { key: gKey, label: gLabel, fields: [field] };
                              byKey.set(gKey, entry);
                              groups.push(entry);
                            });

                            const renderField = (field: any) => {
                              const value = dataFieldValues[step.id]?.[field.key];
                              const isReadOnly = Boolean(field.read_only);
                              const displayValue = Array.isArray(value)
                                ? (value.length ? value.join(', ') : '—')
                                : (value ?? '').toString().trim() || '—';

                              if (!isEditing) {
                                return (
                                  <Box key={field.key} sx={{ py: 0.25 }}>
                                    <Typography sx={{ color: '#9ca3af', fontWeight: 600, fontSize: '0.7rem', lineHeight: 1.3 }}>
                                      {renderFieldLabel(field)}
                                    </Typography>
                                    <Typography sx={{ color: '#111827', fontSize: '0.82rem', mt: 0.15, lineHeight: 1.4 }}>
                                      {displayValue}
                                    </Typography>
                                  </Box>
                                );
                              }

                              if (field.type === 'select') {
                                return (
                                  <FormControl key={field.key} size="small" fullWidth>
                                    <InputLabel>{renderFieldLabel(field)}</InputLabel>
                                    <Select
                                      label={formatLabel(field.label)}
                                      value={value ?? ''}
                                      onChange={(e) => handleDataFieldChange(step.id, field.key, e.target.value)}
                                      disabled={isReadOnly}
                                    >
                                      {field.options?.map((option: string) => (
                                        <MenuItem key={option} value={option}>
                                          {option}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                    {field.helper_text && (
                                      <Typography variant="caption" sx={{ color: '#6b7280', mt: 0.5 }}>
                                        {field.helper_text}
                                      </Typography>
                                    )}
                                  </FormControl>
                                );
                              }

                              if (field.type === 'multiselect') {
                                return (
                                  <FormControl key={field.key} size="small" fullWidth>
                                    <InputLabel>{renderFieldLabel(field)}</InputLabel>
                                    <Select
                                      label={formatLabel(field.label)}
                                      multiple
                                      value={Array.isArray(value) ? value : []}
                                      onChange={(e) => handleDataFieldChange(step.id, field.key, e.target.value)}
                                      renderValue={(selected) => (
                                        (selected as string[]).length === 0 ? (
                                          <Typography variant="body2" sx={{ color: '#9ca3af' }}>
                                            Select...
                                          </Typography>
                                        ) : (
                                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {(selected as string[]).map((item) => (
                                              <Chip key={item} label={item} size="small" />
                                            ))}
                                          </Box>
                                        )
                                      )}
                                      disabled={isReadOnly}
                                    >
                                      {field.options?.map((option: string) => (
                                        <MenuItem key={option} value={option}>
                                          <Checkbox
                                            size="small"
                                            checked={(Array.isArray(value) ? value : []).includes(option)}
                                          />
                                          <ListItemText primary={option} />
                                        </MenuItem>
                                      ))}
                                    </Select>
                                    {field.helper_text && (
                                      <Typography variant="caption" sx={{ color: '#6b7280', mt: 0.5 }}>
                                        {field.helper_text}
                                      </Typography>
                                    )}
                                  </FormControl>
                                );
                              }

                              return (
                                <TextField
                                  key={field.key}
                                  size="small"
                                  label={renderFieldLabel(field)}
                                  required={Boolean(field.required)}
                                  value={value ?? ''}
                                  onChange={(e) => handleDataFieldChange(step.id, field.key, e.target.value)}
                                  fullWidth
                                  disabled={isReadOnly}
                                  multiline={field.type === 'textarea'}
                                  minRows={field.type === 'textarea' ? 3 : undefined}
                                  helperText={field.helper_text}
                                />
                              );
                            };

                            return (
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {groups.map((group) => (
                                  <Box
                                    key={group.key}
                                    sx={{
                                      border: '1px solid #eceef1',
                                      borderRadius: '10px',
                                      bgcolor: '#fafbfc',
                                      px: 1.5,
                                      py: 1,
                                    }}
                                  >
                                    <Typography sx={{ color: '#9ca3af', fontWeight: 650, fontSize: '0.68rem', letterSpacing: '0.03em', textTransform: 'uppercase' }}>
                                      {group.label}
                                    </Typography>
                                    <Box
                                      sx={{
                                        mt: 0.75,
                                        display: 'grid',
                                        gridTemplateColumns: isEditing ? '1fr' : { xs: '1fr', md: '1fr 1fr' },
                                        gap: isEditing ? 1 : 0.75,
                                        rowGap: isEditing ? 1 : 0.5,
                                      }}
                                    >
                                      {group.fields.map(renderField)}
                                    </Box>
                                  </Box>
                                ))}
                              </Box>
                            );
                          })()}
                          {editingInputsStepId === step.id && (
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.75, mt: 0.5 }}>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={async () => {
                                  await handleSaveDataFields(step.id);
                                  setEditingInputsStepId(null);
                                  await handleRunStep(step.id);
                                }}
                                sx={{ fontSize: '0.76rem', px: 1.5, py: 0.25, borderRadius: '8px', textTransform: 'none', fontWeight: 600, borderColor: '#d1d5db', color: '#374151', '&:hover': { borderColor: '#9ca3af', bgcolor: '#f9fafb' } }}
                              >
                                Save
                              </Button>
                              <Button
                                size="small"
                                variant="text"
                                onClick={() => handleCancelInputs(step)}
                                sx={{ fontSize: '0.76rem', px: 1.5, py: 0.25, borderRadius: '8px', textTransform: 'none', color: '#6b7280', '&:hover': { bgcolor: '#f3f4f6' } }}
                              >
                                Cancel
                              </Button>
                            </Box>
                          )}
                        </Box>
                      )}

                      {fields.length === 0 && tasks.length > 0 && !isDocumentWorkspace && (
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                          {step.title?.toLowerCase() === 'evidence mapping' && (
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', pb: 1 }}>
                              <Button
                                size="small"
                                variant="contained"
                                onClick={() => handleRunStep(step.id)}
                                disabled={resultLoadingStepId === step.id}
                                disableElevation
                                sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.78rem', borderRadius: '8px', bgcolor: '#111827', '&:hover': { bgcolor: '#1f2937' } }}
                              >
                                {resultLoadingStepId === step.id
                                  ? (hasGeneratedOutputs ? 'Regenerating…' : 'Generating…')
                                  : (hasGeneratedOutputs ? 'Regenerate Standards Mapping' : 'Generate Standards Mapping')}
                              </Button>
                            </Box>
                          )}
                          {tasks.map((task, index) => (
                            <Box
                              key={task}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.75,
                                py: 0.6,
                                borderBottom: index < tasks.length - 1 ? '1px solid #f3f4f6' : 'none',
                                transition: 'background-color 0.15s ease',
                                '&:hover': {
                                  bgcolor: '#f9fafb',
                                },
                              }}
                            >
                              <Checkbox
                                size="small"
                                checked={taskStatuses[index]}
                                onChange={() => handleToggleTask(step, tasks, index)}
                                sx={{ p: 0.5, color: '#c4c9d0', '&.Mui-checked': { color: '#111827' } }}
                              />
                              <Typography
                                sx={{
                                  color: '#374151',
                                  fontSize: '0.82rem',
                                  fontWeight: 450,
                                  lineHeight: 1.4,
                                }}
                              >
                                {task}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      )}

                      {isDocumentWorkspace && (
                        <Box sx={{ mt: 1 }}>
                          <Typography sx={{ fontWeight: 650, fontSize: '0.82rem', color: '#111827', mb: 0.5 }}>
                            Documents
                          </Typography>
                          <Typography sx={{ color: '#9ca3af', fontSize: '0.78rem', mb: 1, lineHeight: 1.4 }}>
                            All generated files and uploaded documents for this workflow are collected here.
                          </Typography>

                          {(() => {
                            const generated: Array<{
                              stepId: string;
                              stepTitle: string;
                              artifactKey: string;
                              label: string;
                              filename: string;
                              kind: 'csv' | 'xlsx' | 'other';
                            }> = [];
                            const uploaded: Array<{
                              stepId: string;
                              stepTitle: string;
                              url: string;
                              filename: string;
                            }> = [];

                            const seenGenerated = new Set<string>();
                            const seenUploaded = new Set<string>();

                            steps.forEach((s) => {
                              const stepTitle = s.title || 'Step';
                              const cfg: any = s.configuration || {};
                              const artifacts = (cfg && typeof cfg === 'object') ? cfg.artifacts : null;
                              if (artifacts && typeof artifacts === 'object') {
                                Object.keys(artifacts).forEach((k) => {
                                  if (!k) return;
                                  const key = `${s.id}:${k}`;
                                  if (seenGenerated.has(key)) return;
                                  if (!(k.endsWith('_csv') || k.endsWith('_xlsx'))) return;
                                  seenGenerated.add(key);
                                  const kind: any = k.endsWith('_xlsx') ? 'xlsx' : (k.endsWith('_csv') ? 'csv' : 'other');
                                  const filename = k.endsWith('_csv')
                                    ? `${k.replace(/_csv$/i, '').replace(/_/g, '-')}.csv`
                                    : k.endsWith('_xlsx')
                                      ? `${k.replace(/_xlsx$/i, '').replace(/_/g, '-')}.xlsx`
                                      : k;
                                  generated.push({
                                    stepId: s.id,
                                    stepTitle,
                                    artifactKey: k,
                                    label: k,
                                    filename,
                                    kind,
                                  });
                                });
                              }

                              const atts = Array.isArray(s.attachments) ? s.attachments : [];
                              atts.forEach((u) => {
                                const url = `${u || ''}`.trim();
                                if (!url) return;
                                const key = `${s.id}:${url}`;
                                if (seenUploaded.has(key)) return;
                                seenUploaded.add(key);
                                const filename = url.split('/').pop() || 'attachment';
                                uploaded.push({ stepId: s.id, stepTitle, url, filename });
                              });
                            });

                            const groupByStep = <T extends { stepId: string; stepTitle: string }>(items: T[]) => {
                              const map = new Map<string, { title: string; items: T[] }>();
                              items.forEach((it) => {
                                const entry = map.get(it.stepId);
                                if (entry) {
                                  entry.items.push(it);
                                } else {
                                  map.set(it.stepId, { title: it.stepTitle, items: [it] });
                                }
                              });
                              return Array.from(map.entries()).map(([id, v]) => ({ stepId: id, title: v.title, items: v.items }));
                            };

                            const generatedGroups = groupByStep(generated).sort((a, b) => a.title.localeCompare(b.title));
                            const uploadedGroups = groupByStep(uploaded).sort((a, b) => a.title.localeCompare(b.title));

                            return (
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                                <Box sx={{ border: '1px solid #eceef1', borderRadius: '10px', p: 1.25 }}>
                                  <Typography sx={{ color: '#9ca3af', fontWeight: 650, fontSize: '0.68rem', letterSpacing: '0.03em', textTransform: 'uppercase' }}>
                                    Generated files
                                  </Typography>
                                  {generated.length === 0 ? (
                                    <Typography sx={{ color: '#9ca3af', fontSize: '0.78rem', mt: 0.5 }}>
                                      None generated yet.
                                    </Typography>
                                  ) : (
                                    <Box sx={{ mt: 0.75, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                      {generatedGroups.map((g) => (
                                        <Box key={`gen-${g.stepId}`} sx={{ borderTop: '1px solid #f3f4f6', pt: 0.75 }}>
                                          <Typography sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#111827', mb: 0.5 }}>
                                            {g.title}
                                          </Typography>
                                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                            {g.items.map((it) => (
                                              <Box
                                                key={`gen-${it.stepId}-${it.artifactKey}`}
                                                sx={{
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  justifyContent: 'space-between',
                                                  gap: 0.75,
                                                  px: 1,
                                                  py: 0.75,
                                                  border: '1px solid #eceef1',
                                                  borderRadius: '8px',
                                                  bgcolor: '#fafbfc',
                                                }}
                                              >
                                                <Box sx={{ minWidth: 0 }}>
                                                  <Typography sx={{ color: '#111827', fontWeight: 600, fontSize: '0.8rem', overflowWrap: 'anywhere' }}>
                                                    {it.filename}
                                                  </Typography>
                                                  <Typography sx={{ color: '#9ca3af', fontSize: '0.68rem' }}>
                                                    {it.artifactKey}
                                                  </Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                                                  <Button
                                                    size="small"
                                                    variant="outlined"
                                                    startIcon={<DownloadIcon sx={{ fontSize: '0.85rem !important' }} />}
                                                    onClick={() => handleDownloadArtifact(it.stepId, it.artifactKey, it.filename)}
                                                    sx={{ textTransform: 'none', fontSize: '0.74rem', borderRadius: '7px', borderColor: '#d1d5db', color: '#374151', px: 1.25, py: 0.2, '&:hover': { borderColor: '#9ca3af', bgcolor: '#f9fafb' } }}
                                                  >
                                                    Download
                                                  </Button>
                                                  {it.kind === 'csv' && (
                                                    <Button
                                                      size="small"
                                                      variant="contained"
                                                      disableElevation
                                                      onClick={() => openCsvEditor(it.stepId, it.artifactKey, it.filename)}
                                                      sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.74rem', borderRadius: '7px', bgcolor: '#111827', px: 1.25, py: 0.2, '&:hover': { bgcolor: '#1f2937' } }}
                                                    >
                                                      Edit
                                                    </Button>
                                                  )}
                                                </Box>
                                              </Box>
                                            ))}
                                          </Box>
                                        </Box>
                                      ))}
                                    </Box>
                                  )}
                                </Box>

                                <Box sx={{ border: '1px solid #eceef1', borderRadius: '10px', p: 1.25 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                                    <Typography sx={{ color: '#9ca3af', fontWeight: 650, fontSize: '0.68rem', letterSpacing: '0.03em', textTransform: 'uppercase' }}>
                                      Uploaded documents
                                    </Typography>
                                    <input
                                      accept="*/*"
                                      style={{ display: 'none' }}
                                      id={`dw-upload-${step.id}`}
                                      type="file"
                                      onChange={async (e) => {
                                        const file = e.target.files?.[0];
                                        if (!file) return;
                                        try {
                                          await workflowService.uploadStepAttachment(step.id, file);
                                          await fetchWorkflowStatus({ silent: true });
                                        } catch (error) {
                                          window.alert('Failed to upload file. Please try again.');
                                        } finally {
                                          // allow re-uploading the same file by clearing input
                                          (e.target as any).value = '';
                                        }
                                      }}
                                    />
                                    <label htmlFor={`dw-upload-${step.id}`} style={{ cursor: 'pointer' }}>
                                      <Button
                                        component="span"
                                        size="small"
                                        variant="outlined"
                                        startIcon={<AttachFileIcon sx={{ fontSize: '0.85rem !important' }} />}
                                        sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.74rem', borderRadius: '7px', borderColor: '#d1d5db', color: '#374151', px: 1.25, py: 0.2, '&:hover': { borderColor: '#9ca3af', bgcolor: '#f9fafb' } }}
                                      >
                                        Upload
                                      </Button>
                                    </label>
                                  </Box>

                                  {uploaded.length === 0 ? (
                                    <Typography sx={{ color: '#9ca3af', fontSize: '0.78rem', mt: 0.5 }}>
                                      No uploaded documents yet.
                                    </Typography>
                                  ) : (
                                    <Box sx={{ mt: 0.75, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                      {uploadedGroups.map((g) => (
                                        <Box key={`upl-${g.stepId}`} sx={{ borderTop: '1px solid #f3f4f6', pt: 0.75 }}>
                                          <Typography sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#111827', mb: 0.5 }}>
                                            {g.title}
                                          </Typography>
                                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                            {g.items.map((it) => (
                                              <Box
                                                key={`upl-${it.stepId}-${it.url}`}
                                                sx={{
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  justifyContent: 'space-between',
                                                  gap: 0.75,
                                                  px: 1,
                                                  py: 0.6,
                                                  border: '1px solid #eceef1',
                                                  borderRadius: '8px',
                                                  bgcolor: '#fff',
                                                }}
                                              >
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                                                  <AttachFileIcon sx={{ fontSize: 14, color: '#9ca3af' }} />
                                                  <Typography
                                                    sx={{
                                                      color: '#111827',
                                                      fontWeight: 500,
                                                      fontSize: '0.8rem',
                                                      overflow: 'hidden',
                                                      textOverflow: 'ellipsis',
                                                      whiteSpace: 'nowrap',
                                                      cursor: 'pointer',
                                                      '&:hover': { textDecoration: 'underline' },
                                                    }}
                                                    onClick={() => window.open(it.url, '_blank')}
                                                  >
                                                    {it.filename}
                                                  </Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                  <IconButton size="small" onClick={() => window.open(it.url, '_blank')} sx={{ p: 0.5 }}>
                                                    <DownloadIcon sx={{ fontSize: 16 }} />
                                                  </IconButton>
                                                  <IconButton
                                                    size="small"
                                                    onClick={async () => {
                                                      if (!window.confirm('Remove this attachment?')) return;
                                                      try {
                                                        await workflowService.removeStepAttachment(it.stepId, it.url);
                                                        await fetchWorkflowStatus({ silent: true });
                                                      } catch (error) {
                                                        window.alert('Failed to remove attachment. Please try again.');
                                                      }
                                                    }}
                                                    sx={{ p: 0.5, color: 'error.main' }}
                                                  >
                                                    <DeleteIcon sx={{ fontSize: 16 }} />
                                                  </IconButton>
                                                </Box>
                                              </Box>
                                            ))}
                                          </Box>
                                        </Box>
                                      ))}
                                    </Box>
                                  )}
                                </Box>
                              </Box>
                            );
                          })()}
                        </Box>
                      )}

                      {results.length > 0 && (
                        <Box sx={{ mt: 1.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
                            <Typography sx={{ fontWeight: 650, fontSize: '0.82rem', color: '#111827' }}>
                              Result
                            </Typography>
                            {(visibleFields.length === 0 || hasEnteredFormData || hasGeneratedOutputs) && (
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => handleRunStep(step.id)}
                                disabled={resultLoadingStepId === step.id}
                                sx={{
                                  textTransform: 'none',
                                  fontWeight: 600,
                                  fontSize: '0.76rem',
                                  borderRadius: '8px',
                                  borderColor: '#d1d5db',
                                  color: '#374151',
                                  px: 1.5,
                                  py: 0.25,
                                  '&:hover': { borderColor: '#9ca3af', bgcolor: '#f9fafb' },
                                }}
                              >
                                {resultLoadingStepId === step.id
                                  ? (hasGeneratedOutputs ? 'Regenerating…' : 'Generating…')
                                  : (hasGeneratedOutputs ? 'Regenerate' : 'Generate')}
                              </Button>
                            )}
                          </Box>
                          {resultLoadingStepId === step.id && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
                              <CircularProgress size={14} />
                              <Typography sx={{ color: '#9ca3af', fontSize: '0.78rem' }}>
                                Generating results...
                              </Typography>
                            </Box>
                          )}
                          {(() => {
                            const isBusy = savingInputsStepId === step.id || resultLoadingStepId === step.id || editingInputsStepId === step.id;
                            if (isBusy || links.length === 0) return null;
                            return (
                            <Box sx={{ mb: 0.75 }}>
                              {links.map((link) => (
                                <Typography
                                  key={link.key}
                                  sx={{
                                    color: '#2563eb',
                                    fontWeight: 500,
                                    fontSize: '0.8rem',
                                    cursor: 'pointer',
                                    textDecoration: 'underline',
                                    display: 'inline-block',
                                    '&:hover': { color: '#1d4ed8' },
                                  }}
                                  onClick={() => {
                                    if (link.target_tab === 'eudamed' && onOpenEudamedTab) {
                                      onOpenEudamedTab();
                                    }
                                  }}
                                >
                                  {link.label}
                                </Typography>
                              ))}
                            </Box>
                            );
                          })()}
                          {(() => {
                            const isSubmissionGuidance = (step.title || '').toLowerCase() === 'submission guidance';
                            const vals = dataFieldValues[step.id] || {};

                            if (isSubmissionGuidance) {
                              const timeline = vals.submission_timeline_estimate as string | undefined;
                              const roadmap: string[] = Array.isArray(vals.submission_roadmap) ? vals.submission_roadmap : [];
                              const eudamed: string[] = Array.isArray(vals.eudamed_modules) ? vals.eudamed_modules : [];
                              const nbDeliverables: string[] = Array.isArray(vals.nb_deliverables) ? vals.nb_deliverables : [];
                              const tdSections: string[] = Array.isArray(vals.technical_documentation_sections) ? vals.technical_documentation_sections : [];
                              const blockingDeps: string[] = Array.isArray(vals.blocking_dependencies) ? vals.blocking_dependencies : [];
                              const pitfalls: string[] = Array.isArray(vals.common_pitfalls) ? vals.common_pitfalls : [];
                              const portals: string[] = Array.isArray(vals.submission_portals) ? vals.submission_portals : [];
                              const contacts: string[] = Array.isArray(vals.authority_contacts) ? vals.authority_contacts : [];
                              const refDocs: string[] = Array.isArray(vals.reference_documents) ? vals.reference_documents : [];
                              const hasAnyData = timeline || roadmap.length > 0 || eudamed.length > 0;

                              if (!hasAnyData) {
                                return (
                                  <Typography sx={{ color: '#9ca3af', mt: 0.5, fontSize: '0.82rem' }}>
                                    Not generated yet
                                  </Typography>
                                );
                              }

                              const sectionBox = { border: '1px solid #eceef1', borderRadius: '10px', bgcolor: '#fff', px: 1.5, py: 1.25 };
                              const sectionLabel = { color: '#9ca3af', fontWeight: 650, fontSize: '0.68rem', letterSpacing: '0.03em', textTransform: 'uppercase' as const, mb: 0.75 };

                              const getEudamedChipColors = (status: string) => {
                                const s = status.toLowerCase().trim();
                                if (s === 'required') return { bgcolor: '#dcfce7', color: '#166534' };
                                if (s === 'not applicable') return { bgcolor: '#f3f4f6', color: '#6b7280' };
                                return { bgcolor: '#fef3c7', color: '#92400e' };
                              };

                              return (
                                <Box sx={{ width: '100%', maxWidth: 980, display: 'flex', flexDirection: 'column', gap: 1.25 }}>

                                  {timeline && (
                                    <Box sx={sectionBox}>
                                      <Typography sx={sectionLabel}>Estimated Overall Timeline</Typography>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <ScheduleIcon sx={{ fontSize: 18, color: '#6b7280' }} />
                                        <Typography sx={{ fontWeight: 650, fontSize: '0.92rem', color: '#111827' }}>
                                          {timeline.replace(/\s*\(.*\)\s*$/, '')}
                                        </Typography>
                                      </Box>
                                      {timeline.includes('(') && (
                                        <Typography sx={{ fontSize: '0.76rem', color: '#9ca3af', mt: 0.25, ml: 3.5 }}>
                                          {timeline.match(/\(([^)]+)\)/)?.[1]}
                                        </Typography>
                                      )}
                                    </Box>
                                  )}

                                  {roadmap.length > 0 && (
                                    <Box sx={sectionBox}>
                                      <Typography sx={sectionLabel}>Submission Roadmap</Typography>
                                      <Box sx={{ display: 'flex', flexDirection: 'column', pl: 0.5 }}>
                                        {roadmap.map((item, idx) => {
                                          const m = item.match(/^Step\s*(\d+):\s*(.+?)\s*\(([^)]+)\)(?:\s*[—–-]\s*(.+))?$/);
                                          const stepNum = m?.[1] || `${idx + 1}`;
                                          const action = m?.[2] || item;
                                          const timeRange = m?.[3];
                                          const notes = m?.[4];
                                          const isLast = idx === roadmap.length - 1;
                                          return (
                                            <Box key={idx} sx={{ display: 'flex', gap: 1.25, minHeight: 40 }}>
                                              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 22, flexShrink: 0 }}>
                                                <Box sx={{
                                                  width: 22, height: 22, borderRadius: '50%', bgcolor: '#111827', color: '#fff',
                                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                  fontSize: '0.62rem', fontWeight: 700, flexShrink: 0,
                                                }}>
                                                  {stepNum}
                                                </Box>
                                                {!isLast && <Box sx={{ width: 1.5, flexGrow: 1, bgcolor: '#e5e7eb', mt: 0.5, mb: 0.5, borderRadius: 1 }} />}
                                              </Box>
                                              <Box sx={{ pb: isLast ? 0 : 1.25, pt: 0.15 }}>
                                                <Typography sx={{ fontWeight: 600, fontSize: '0.82rem', color: '#111827', lineHeight: 1.3 }}>
                                                  {action}
                                                </Typography>
                                                {timeRange && (
                                                  <Box component="span" sx={{
                                                    display: 'inline-block', mt: 0.3,
                                                    fontSize: '0.66rem', fontWeight: 600, color: '#6b7280',
                                                    bgcolor: '#f3f4f6', px: 0.75, py: 0.15, borderRadius: '4px',
                                                  }}>
                                                    {timeRange}
                                                  </Box>
                                                )}
                                                {notes && (
                                                  <Typography sx={{ fontSize: '0.76rem', color: '#9ca3af', mt: 0.25, lineHeight: 1.35 }}>
                                                    {notes}
                                                  </Typography>
                                                )}
                                              </Box>
                                            </Box>
                                          );
                                        })}
                                      </Box>
                                    </Box>
                                  )}

                                  {eudamed.length > 0 && (
                                    <Box sx={sectionBox}>
                                      <Typography sx={sectionLabel}>EUDAMED Module Mapping</Typography>
                                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                                        {eudamed.map((item, idx) => {
                                          const colonIdx = item.indexOf(':');
                                          const moduleName = colonIdx > -1 ? item.substring(0, colonIdx).trim() : item;
                                          const statusStr = colonIdx > -1 ? item.substring(colonIdx + 1).trim() : '';
                                          const chipColors = getEudamedChipColors(statusStr);
                                          return (
                                            <Box key={idx} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.5, px: 0.5, borderRadius: '6px', '&:hover': { bgcolor: '#fafbfc' } }}>
                                              <Typography sx={{ fontSize: '0.82rem', color: '#374151', fontWeight: 500 }}>
                                                {moduleName}
                                              </Typography>
                                              {statusStr && (
                                                <Box sx={{ px: 0.75, py: 0.15, borderRadius: '4px', fontSize: '0.66rem', fontWeight: 600, ...chipColors }}>
                                                  {statusStr}
                                                </Box>
                                              )}
                                            </Box>
                                          );
                                        })}
                                      </Box>
                                    </Box>
                                  )}

                                  {nbDeliverables.length > 0 && (
                                    <Box sx={sectionBox}>
                                      <Typography sx={sectionLabel}>Notified Body Deliverables</Typography>
                                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.35 }}>
                                        {nbDeliverables.map((item, idx) => (
                                          <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, py: 0.3 }}>
                                            <CheckCircleOutlineIcon sx={{ fontSize: 15, color: '#22c55e', mt: '2px', flexShrink: 0 }} />
                                            <Typography sx={{ fontSize: '0.82rem', color: '#374151', lineHeight: 1.4 }}>
                                              {item}
                                            </Typography>
                                          </Box>
                                        ))}
                                      </Box>
                                    </Box>
                                  )}

                                  {tdSections.length > 0 && (
                                    <Box sx={sectionBox}>
                                      <Typography sx={sectionLabel}>Technical Documentation Sections</Typography>
                                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                                        {tdSections.map((item, idx) => {
                                          const tdMatch = item.match(/^Section\s*(\w+):\s*(.+?)\s*[—–-]\s*(.+)$/);
                                          const sectionNum = tdMatch?.[1] || `${idx + 1}`;
                                          const title = tdMatch?.[2] || item;
                                          const status = tdMatch?.[3];
                                          return (
                                            <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, py: 0.4, px: 0.5, borderRadius: '6px', '&:hover': { bgcolor: '#fafbfc' } }}>
                                              <Box sx={{
                                                width: 20, height: 20, borderRadius: '50%', bgcolor: '#f3f4f6',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                fontSize: '0.6rem', fontWeight: 700, color: '#6b7280', flexShrink: 0,
                                              }}>
                                                {sectionNum}
                                              </Box>
                                              <Typography sx={{ fontSize: '0.82rem', color: '#374151', fontWeight: 500, flex: 1 }}>
                                                {title}
                                              </Typography>
                                              {status && (
                                                <Box sx={{ px: 0.75, py: 0.15, borderRadius: '4px', fontSize: '0.66rem', fontWeight: 600, bgcolor: '#f0fdf4', color: '#166534' }}>
                                                  {status}
                                                </Box>
                                              )}
                                            </Box>
                                          );
                                        })}
                                      </Box>
                                    </Box>
                                  )}

                                  {blockingDeps.length > 0 && (
                                    <Box sx={sectionBox}>
                                      <Typography sx={sectionLabel}>Blocking Dependencies</Typography>
                                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.35 }}>
                                        {blockingDeps.map((item, idx) => (
                                          <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, py: 0.3 }}>
                                            <WarningAmberIcon sx={{ fontSize: 15, color: '#f59e0b', mt: '2px', flexShrink: 0 }} />
                                            <Typography sx={{ fontSize: '0.82rem', color: '#374151', lineHeight: 1.4 }}>
                                              {item}
                                            </Typography>
                                          </Box>
                                        ))}
                                      </Box>
                                    </Box>
                                  )}

                                  {(portals.length > 0 || contacts.length > 0) && (
                                    <Box sx={sectionBox}>
                                      <Typography sx={sectionLabel}>Portals, Resources & Contacts</Typography>
                                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 0.75 }}>
                                        {[...portals, ...contacts].map((item, idx) => {
                                          const parts = item.split('|');
                                          const name = parts[0] || '';
                                          const url = parts[1] || '';
                                          const desc = parts[2] || '';
                                          const isAR = name.includes('Authorized Representative');
                                          return (
                                            <Box key={idx} sx={{ py: 0.5, px: 0.75, borderRadius: '6px', border: '1px solid #eceef1', ...(isAR ? { bgcolor: '#fef3c7', borderColor: '#fde68a' } : { bgcolor: '#fafbfc' }) }}>
                                              <Typography
                                                component="a"
                                                href={url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                sx={{ fontSize: '0.78rem', color: isAR ? '#92400e' : '#2563eb', fontWeight: 600, textDecoration: 'none', '&:hover': { textDecoration: 'underline' }, display: 'inline-flex', alignItems: 'center', gap: 0.4 }}
                                              >
                                                {name}
                                                <OpenInNewIcon sx={{ fontSize: 11 }} />
                                              </Typography>
                                              {desc && (
                                                <Typography sx={{ fontSize: '0.72rem', color: isAR ? '#78350f' : '#9ca3af', lineHeight: 1.3, mt: 0.1 }}>
                                                  {desc}
                                                </Typography>
                                              )}
                                            </Box>
                                          );
                                        })}
                                      </Box>
                                    </Box>
                                  )}

                                  {refDocs.length > 0 && (
                                    <Box sx={sectionBox}>
                                      <Typography sx={sectionLabel}>Key Reference Documents</Typography>
                                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 0.5 }}>
                                        {refDocs.map((item, idx) => {
                                          const parts = item.split('|');
                                          const dTitle = parts[0] || '';
                                          const dUrl = parts[1] || '';
                                          const dRelevance = parts[2] || '';
                                          return (
                                            <Box key={idx} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.75, py: 0.35, px: 0.5, borderRadius: '6px', '&:hover': { bgcolor: '#fafbfc' } }}>
                                              <Typography
                                                component="a"
                                                href={dUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                sx={{ fontSize: '0.78rem', color: '#2563eb', fontWeight: 500, textDecoration: 'none', '&:hover': { textDecoration: 'underline' }, display: 'inline-flex', alignItems: 'center', gap: 0.4, flex: 1, minWidth: 0 }}
                                              >
                                                {dTitle}
                                                <OpenInNewIcon sx={{ fontSize: 11, flexShrink: 0 }} />
                                              </Typography>
                                              {dRelevance && (
                                                <Box sx={{ px: 0.6, py: 0.1, borderRadius: '4px', fontSize: '0.64rem', fontWeight: 600, bgcolor: '#f3f4f6', color: '#6b7280', whiteSpace: 'nowrap', flexShrink: 0 }}>
                                                  {dRelevance}
                                                </Box>
                                              )}
                                            </Box>
                                          );
                                        })}
                                      </Box>
                                    </Box>
                                  )}

                                  {pitfalls.length > 0 && (
                                    <Box sx={{ ...sectionBox, bgcolor: '#fffbeb', borderColor: '#fde68a' }}>
                                      <Typography sx={{ ...sectionLabel, color: '#92400e' }}>Common Pitfalls & Watch-Outs</Typography>
                                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                        {pitfalls.map((item, idx) => (
                                          <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, py: 0.3 }}>
                                            <LightbulbOutlinedIcon sx={{ fontSize: 15, color: '#d97706', mt: '2px', flexShrink: 0 }} />
                                            <Typography sx={{ fontSize: '0.82rem', color: '#78350f', lineHeight: 1.4 }}>
                                              {item}
                                            </Typography>
                                          </Box>
                                        ))}
                                      </Box>
                                    </Box>
                                  )}

                                </Box>
                              );
                            }

                            const isEvidenceMapping = results.some((r: any) => r.key === 'standards_selected');

                            if (isEvidenceMapping) {
                              const dv = dataFieldValues[step.id] || {};
                              const standardsList: Array<{ code: string; justification: string }> = (() => {
                                const raw = dv.standards_selected;
                                if (!Array.isArray(raw) || raw.length === 0) return [];
                                if (typeof raw[0] === 'object' && raw[0]?.code) return raw;
                                return raw.map((v: any) => ({ code: String(v), justification: '' }));
                              })();
                              const smFiles: any[] = Array.isArray(dv.standards_mapping_csv) ? dv.standards_mapping_csv : [];
                              const tvjFiles: any[] = Array.isArray(dv.test_vs_justification_csv) ? dv.test_vs_justification_csv : [];
                              const hasXlsx = !!dv.evidence_mapping_workbook_xlsx;
                              const isBusy = savingInputsStepId === step.id || resultLoadingStepId === step.id || editingInputsStepId === step.id;

                              const findArtifact = (files: any[], stdCode: string) =>
                                files.find((f: any) => f.standard === stdCode || f.label === stdCode);

                              return (
                                <Box sx={{ width: '100%', maxWidth: 980 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                    <Typography sx={{ color: '#9ca3af', fontWeight: 650, fontSize: '0.68rem', letterSpacing: '0.03em', textTransform: 'uppercase' }}>
                                      {standardsList.length} standard{standardsList.length !== 1 ? 's' : ''} applicable
                                    </Typography>
                                    <Button
                                      size="small"
                                      variant="text"
                                      onClick={() => handleRunStep(step.id)}
                                      disabled={isBusy}
                                      sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.74rem', color: '#6b7280', '&:hover': { bgcolor: '#f3f4f6', color: '#374151' } }}
                                    >
                                      {resultLoadingStepId === step.id ? 'Regenerating…' : 'Regenerate All'}
                                    </Button>
                                  </Box>

                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    {standardsList.map((std) => {
                                      const smArtifact = findArtifact(smFiles, std.code);
                                      const tvjArtifact = findArtifact(tvjFiles, std.code);

                                      return (
                                        <Accordion
                                          key={std.code}
                                          disableGutters
                                          elevation={0}
                                          sx={{
                                            border: '1px solid #eceef1',
                                            borderRadius: '8px !important',
                                            '&:before': { display: 'none' },
                                            '&.Mui-expanded': { margin: 0 },
                                          }}
                                        >
                                          <AccordionSummary
                                            expandIcon={<ExpandMoreIcon sx={{ fontSize: 16, color: '#9ca3af' }} />}
                                            sx={{
                                              minHeight: 0,
                                              px: 1.5,
                                              py: 0.5,
                                              '& .MuiAccordionSummary-content': { margin: '6px 0', alignItems: 'center', gap: 1, flexWrap: 'wrap' },
                                              '&.Mui-expanded': { minHeight: 0 },
                                            }}
                                          >
                                            <CheckCircleOutlineIcon sx={{ fontSize: 15, color: '#16a34a', flexShrink: 0 }} />
                                            <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#111827', flexShrink: 0 }}>
                                              {std.code}
                                            </Typography>
                                          </AccordionSummary>
                                          <AccordionDetails sx={{ px: 1.5, pt: 0, pb: 1.25 }}>
                                            {std.justification && (
                                              <Typography sx={{ fontSize: '0.74rem', color: '#6b7280', lineHeight: 1.5, mb: 1.25, pl: 3 }}>
                                                {std.justification}
                                              </Typography>
                                            )}
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', pl: 3 }}>
                                              {smArtifact && (
                                                <>
                                                  <Button
                                                    size="small"
                                                    variant="outlined"
                                                    startIcon={<DownloadIcon sx={{ fontSize: '0.8rem !important' }} />}
                                                    onClick={() => handleDownloadArtifact(step.id, smArtifact.artifact_key, smArtifact.filename || 'standards-mapping.csv')}
                                                    disabled={isBusy}
                                                    sx={{ ...artifactBtnSx, fontSize: '0.7rem', px: 1 }}
                                                  >
                                                    Standards Mapping
                                                  </Button>
                                                  <Button
                                                    size="small"
                                                    variant="outlined"
                                                    startIcon={<EditIcon sx={{ fontSize: '0.8rem !important' }} />}
                                                    onClick={() => openCsvEditor(step.id, smArtifact.artifact_key, `Standards Mapping — ${std.code}`)}
                                                    disabled={isBusy}
                                                    sx={{ ...artifactBtnSx, fontSize: '0.7rem', px: 1 }}
                                                  >
                                                    Edit
                                                  </Button>
                                                </>
                                              )}
                                              {tvjArtifact && (
                                                <>
                                                  <Box sx={{ width: '1px', height: 18, bgcolor: '#e5e7eb', mx: 0.25 }} />
                                                  <Button
                                                    size="small"
                                                    variant="outlined"
                                                    startIcon={<DownloadIcon sx={{ fontSize: '0.8rem !important' }} />}
                                                    onClick={() => handleDownloadArtifact(step.id, tvjArtifact.artifact_key, tvjArtifact.filename || 'test-vs-justification.csv')}
                                                    disabled={isBusy}
                                                    sx={{ ...artifactBtnSx, fontSize: '0.7rem', px: 1 }}
                                                  >
                                                    Test vs Justification
                                                  </Button>
                                                  <Button
                                                    size="small"
                                                    variant="outlined"
                                                    startIcon={<EditIcon sx={{ fontSize: '0.8rem !important' }} />}
                                                    onClick={() => openCsvEditor(step.id, tvjArtifact.artifact_key, `Test vs Justification — ${std.code}`)}
                                                    disabled={isBusy}
                                                    sx={{ ...artifactBtnSx, fontSize: '0.7rem', px: 1 }}
                                                  >
                                                    Edit
                                                  </Button>
                                                </>
                                              )}
                                            </Box>
                                          </AccordionDetails>
                                        </Accordion>
                                      );
                                    })}
                                  </Box>

                                  {hasXlsx && (
                                    <Box sx={{ mt: 1.25, border: '1px solid #eceef1', borderRadius: '8px', px: 1.5, py: 1, bgcolor: '#fafbfc', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                      <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#374151' }}>
                                        Evidence Mapping Workbook
                                      </Typography>
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<DownloadIcon sx={{ fontSize: '0.85rem !important' }} />}
                                        onClick={() => handleDownloadArtifact(step.id, 'evidence_mapping_workbook_xlsx', 'evidence-mapping-workbook.xlsx')}
                                        disabled={isBusy}
                                        sx={artifactBtnSx}
                                      >
                                        Download XLSX
                                      </Button>
                                    </Box>
                                  )}
                                </Box>
                              );
                            }

                            const resultGroups: Array<{ key: string; label: string; results: any[] }> = [];
                            const byKey = new Map<string, { key: string; label: string; results: any[] }>();
                            results.forEach((result: any) => {
                              const gKey = `${result.group || 'results'}`;
                              const gLabel = `${result.group_label || result.group || 'Results'}`;
                              const existing = byKey.get(gKey);
                              if (existing) {
                                existing.results.push(result);
                                return;
                              }
                              const entry = { key: gKey, label: gLabel, results: [result] };
                              byKey.set(gKey, entry);
                              resultGroups.push(entry);
                            });

                            const renderResultCard = (result: any) => {
                              return (
                              <Box
                                key={result.key}
                                sx={{
                                  border: '1px solid #eceef1',
                                  borderRadius: '8px',
                                  px: 1.25,
                                  py: 0.9,
                                  bgcolor: '#fafbfc',
                                  minHeight: 56,
                                }}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.75 }}>
                                  <Typography sx={{ color: '#9ca3af', fontWeight: 650, fontSize: '0.7rem' }}>
                                    {formatResultLabel(result)}
                                  </Typography>
                                </Box>
                                {renderResultValue(step.id, result.key, dataFieldValues[step.id]?.[result.key])}
                              </Box>
                            );
                            };

                            return (
                              <Box sx={{ width: '100%', maxWidth: 980 }}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                  {resultGroups.map((group) => {
                                    const isSingleColumn = group.results.length === 1;
                                    return (
                                      <Box
                                        key={group.key}
                                        sx={{
                                          border: '1px solid #eceef1',
                                          borderRadius: '10px',
                                          bgcolor: '#fff',
                                          px: 1.5,
                                          py: 1.25,
                                        }}
                                      >
                                        <Typography
                                          sx={{ color: '#9ca3af', fontWeight: 650, fontSize: '0.68rem', letterSpacing: '0.03em', textTransform: 'uppercase', mb: 0.75 }}
                                        >
                                          {group.label}
                                        </Typography>
                                        <Box
                                          sx={{
                                            display: 'grid',
                                            gridTemplateColumns: isSingleColumn ? '1fr' : { xs: '1fr', md: '1fr 1fr' },
                                            gap: 0.75,
                                          }}
                                        >
                                          {group.results.map(renderResultCard)}
                                        </Box>
                                      </Box>
                                    );
                                  })}
                                </Box>
                              </Box>
                            );
                          })()}
                        </Box>
                      )}
                      {results.length === 0 && outputs.length > 0 && (
                        <Box sx={{ mt: 1.5 }}>
                          <Typography sx={{ fontWeight: 650, fontSize: '0.82rem', color: '#111827', mb: 0.75 }}>
                            Outputs
                          </Typography>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            {outputs.map((item) => {
                              const lowered = item.toLowerCase();
                              const type = lowered.includes('form')
                                ? 'Form'
                                : lowered.includes('information') || lowered.includes('info')
                                  ? 'Information'
                                  : 'File';
                              return (
                                <Box
                                  key={item}
                                  sx={{
                                    border: '1px solid #eceef1',
                                    borderRadius: '8px',
                                    px: 1.25,
                                    py: 0.9,
                                    bgcolor: '#fafbfc',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                  }}
                                >
                                  <Box>
                                    <Typography sx={{ color: '#9ca3af', fontWeight: 600, fontSize: '0.68rem' }}>
                                      {type}
                                    </Typography>
                                    <Typography sx={{ color: '#374151', fontSize: '0.82rem', mt: 0.15 }}>
                                      {item}
                                    </Typography>
                                  </Box>
                                </Box>
                              );
                            })}
                          </Box>
                        </Box>
                      )}
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {workflowInputs.length > 0 && (
                <Card
                  sx={{
                    borderRadius: 1.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                  }}
                >
                  <CardContent sx={{ p: 1.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          Workflow Inputs
                        </Typography>
                        {workflowInputsMode === 'view' && (
                          <Typography variant="caption" color="success.main">
                            Saved
                          </Typography>
                        )}
                      </Box>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={workflowInputsMode === 'edit' ? handleSaveWorkflowInputs : () => setWorkflowInputsMode('edit')}
                        sx={{ fontSize: '0.75rem', px: 1.5 }}
                        startIcon={workflowInputsMode === 'edit' ? <SaveIcon fontSize="small" /> : <EditIcon fontSize="small" />}
                      >
                        {workflowInputsMode === 'edit' ? 'Save' : 'Edit'}
                      </Button>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {workflowInputs.map((input, idx) => (
                        <TextField
                          key={input.key}
                          size="small"
                          label={input.label}
                          value={input.value}
                          onChange={(e) => handleWorkflowInputChange(idx, e.target.value)}
                          fullWidth
                          disabled={workflowInputsMode === 'view'}
                        />
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              )}
              {steps.map((step, index) => {
                const statusConfig = getStatusConfig(step.status);
                return (
                  <Card
                    key={step.id}
                    sx={{
                      borderRadius: 1.5,
                      border: '1px solid',
                      borderColor: 'divider',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                        borderColor: alpha(statusConfig.color, 0.3),
                      }
                    }}
                  >
                    <CardContent sx={{ p: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                            {editingStepId === step.id ? (
                              <TextField
                                value={editingFields.title || ''}
                                onChange={(e) => setEditingFields({ ...editingFields, title: e.target.value })}
                                size="small"
                                sx={{
                                  flex: 1,
                                  minWidth: 200,
                                  '& .MuiInputBase-root': {
                                    fontSize: '0.9rem',
                                    fontWeight: 600,
                                  }
                                }}
                                placeholder="Step title"
                              />
                            ) : (
                              <Typography
                                variant="subtitle1"
                                component="div"
                                sx={{
                                  fontWeight: 600,
                                  fontSize: '0.9rem',
                                  color: 'text.primary'
                                }}
                              >
                                Step {step.step_number}: {step.title}
                              </Typography>
                            )}
                            <Chip
                              label={statusConfig.label}
                              size="small"
                              sx={{
                                height: 20,
                                fontWeight: 600,
                                fontSize: '0.6875rem',
                                letterSpacing: '0.4px',
                                backgroundColor: statusConfig.bgColor,
                                color: statusConfig.color,
                                border: `1px solid ${alpha(statusConfig.color, 0.3)}`,
                                '& .MuiChip-label': {
                                  px: 1,
                                }
                              }}
                            />
                          </Box>

                          <Box
                            sx={{
                              display: 'flex',
                              gap: 1.5,
                              mb: 1,
                              flexWrap: 'wrap',
                              alignItems: 'center'
                            }}
                          >
                            {editingStepId === step.id ? (
                              <>
                                <TextField
                                  type="number"
                                  label="Estimated Days"
                                  value={editingFields.estimated_days || ''}
                                  onChange={(e) => setEditingFields({ ...editingFields, estimated_days: parseInt(e.target.value) || 0 })}
                                  size="small"
                                  sx={{ width: 120 }}
                                  InputProps={{
                                    endAdornment: <InputAdornment position="end">days</InputAdornment>,
                                  }}
                                />
                                <TextField
                                  type="date"
                                  label="Due Date"
                                  value={editingFields.due_date || ''}
                                  onChange={(e) => setEditingFields({ ...editingFields, due_date: e.target.value })}
                                  size="small"
                                  sx={{ width: 160 }}
                                  InputLabelProps={{ shrink: true }}
                                />
                              </>
                            ) : (
                              <>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                                    Estimated:
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: 'text.primary', fontWeight: 600, fontSize: '0.8125rem' }}>
                                    {step.estimated_days} days
                                  </Typography>
                                </Box>
                                {step.actual_days && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                                      Actual:
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: 'text.primary', fontWeight: 600, fontSize: '0.8125rem' }}>
                                      {step.actual_days} days
                                    </Typography>
                                  </Box>
                                )}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                                    Due:
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: 'text.primary', fontWeight: 600, fontSize: '0.8125rem' }}>
                                    {formatDate(step.due_date)}
                                  </Typography>
                                </Box>
                              </>
                            )}
                          </Box>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          {editingStepId !== step.id && (
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                              {(/classification|risk assessment/.test(step.title.toLowerCase())) && (
                                <Button
                                  variant="contained"
                                  size="small"
                                  onClick={() => handleRunStep(step.id)}
                                  disabled={!isStepReadyForRun(step) || step.status === 'completed'}
                                  sx={{
                                    textTransform: 'none',
                                    minWidth: 90,
                                    fontWeight: 600,
                                    fontSize: '0.8125rem',
                                    py: 0.75,
                                    boxShadow: 'none',
                                    '&:hover': {
                                      boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
                                    }
                                  }}
                                >
                                  Start
                                </Button>
                              )}
                              <FormControl size="small" sx={{ minWidth: 150 }}>
                                <InputLabel
                                  sx={{
                                    fontSize: '0.8125rem',
                                    '&.Mui-focused': {
                                      fontSize: '0.8125rem',
                                    }
                                  }}
                                >
                                  Status
                                </InputLabel>
                                <Select
                                  value={stepStatusChanges[step.id] || step.status}
                                  onChange={(e) => handleStatusChange(step.id, e.target.value)}
                                  label="Status"
                                  sx={{
                                    fontSize: '0.8125rem',
                                    height: '36px',
                                    '& .MuiSelect-select': {
                                      py: 0.75,
                                      fontSize: '0.8125rem',
                                    }
                                  }}
                                >
                                  {[step.status, ...getAvailableStatusOptions(step.status)].map((status) => (
                                    <MenuItem
                                      key={status}
                                      value={status}
                                      sx={{ fontSize: '0.8125rem' }}
                                    >
                                      {status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>

                              {stepStatusChanges[step.id] && stepStatusChanges[step.id] !== step.status && (
                                <Button
                                  variant="contained"
                                  onClick={() => {
                                    const newStatus = stepStatusChanges[step.id];
                                    if (newStatus === 'in_progress') {
                                      handleStartStep(step.id);
                                    } else if (newStatus === 'completed' && step.status === 'in_progress') {
                                      handleCompleteStep(step.id);
                                    } else if (newStatus === 'skipped') {
                                      handleSkipStep(step.id);
                                    } else {
                                      handleUpdateStepStatus(step.id, newStatus);
                                    }
                                  }}
                                  size="small"
                                  sx={{
                                    textTransform: 'none',
                                    minWidth: 86,
                                    fontWeight: 500,
                                    fontSize: '0.8125rem',
                                    py: 0.75,
                                    boxShadow: 'none',
                                    '&:hover': {
                                      boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
                                    }
                                  }}
                                >
                                  Apply
                                </Button>
                              )}
                            </Box>
                          )}

                          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                            {editingStepId === step.id ? (
                              <>
                                <Tooltip title="Save changes">
                                  <IconButton
                                    onClick={() => handleSaveEdit(step.id)}
                                    size="small"
                                    sx={{ color: 'primary.main' }}
                                  >
                                    <SaveIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Cancel edit">
                                  <IconButton
                                    onClick={handleCancelEdit}
                                    size="small"
                                    sx={{ color: 'text.secondary' }}
                                  >
                                    <CancelIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </>
                            ) : (
                              <>
                                <Tooltip title="Edit step">
                                  <IconButton
                                    onClick={() => handleStartEdit(step)}
                                    size="small"
                                    sx={{ color: 'primary.main' }}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title={step.comments ? 'Edit comments' : 'Add comments'}>
                                  <IconButton
                                    onClick={() => handleCommentsClick(step)}
                                    size="small"
                                    sx={{ color: 'text.secondary' }}
                                  >
                                    <CommentIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete step">
                                  <span>
                                    <IconButton
                                      onClick={() => handleDeleteStep(step.id)}
                                      size="small"
                                      disabled={deletingStepId === step.id}
                                      sx={{ color: 'error.main' }}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </>
                            )}
                          </Box>
                        </Box>
                      </Box>

                      {step.solution && (
                        <Box
                          sx={{
                            mb: 1,
                            p: 1,
                            bgcolor: 'primary.50',
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'primary.200'
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              mb: 0.75,
                              fontWeight: 600,
                              color: 'primary.main',
                              fontSize: '0.6875rem',
                              textTransform: 'uppercase',
                              letterSpacing: '0.4px'
                            }}
                          >
                            Solution & Guidance
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: 'text.primary',
                              lineHeight: 1.6,
                              whiteSpace: 'pre-wrap',
                              fontSize: '0.8125rem'
                            }}
                          >
                            {step.solution}
                          </Typography>
                        </Box>
                      )}

                      {step.comments && (
                        <Box
                          sx={{
                            mb: 1,
                            p: 1,
                            bgcolor: 'grey.50',
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'divider'
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              mb: 0.75,
                              fontWeight: 600,
                              color: 'text.secondary',
                              fontSize: '0.6875rem',
                              textTransform: 'uppercase',
                              letterSpacing: '0.4px'
                            }}
                          >
                            Comments
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: 'text.primary',
                              lineHeight: 1.6,
                              whiteSpace: 'pre-wrap',
                              fontSize: '0.8125rem'
                            }}
                          >
                            {step.comments}
                          </Typography>
                        </Box>
                      )}

                      {step.required_forms.length > 0 && (
                        <Box sx={{ mb: 1 }}>
                          <Typography
                            variant="caption"
                            sx={{
                              mb: 0.75,
                              fontWeight: 600,
                              color: 'text.secondary',
                              fontSize: '0.6875rem',
                              textTransform: 'uppercase',
                              letterSpacing: '0.4px',
                              display: 'block'
                            }}
                          >
                            Required Forms
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {step.required_forms.map((form, idx) => {
                              // Try to find form download URL from configuration
                              let formUrl: string | undefined;
                              const formDownloads = step.configuration?.form_downloads || [];
                              const matchingForm = formDownloads.find(
                                (f: any) => (f.form_code || f.form_name || '').toUpperCase() === form.toUpperCase()
                              );
                              if (matchingForm) {
                                formUrl = matchingForm.form_download_url || matchingForm.download_url;
                              } else if (step.configuration?.form_filling && step.configuration?.form_code?.toUpperCase() === form.toUpperCase()) {
                                formUrl = step.configuration?.form_download_url;
                              }

                              return (
                                <Chip
                                  key={idx}
                                  label={form}
                                  size="small"
                                  variant="outlined"
                                  onClick={formUrl ? () => window.open(formUrl, '_blank') : undefined}
                                  sx={{
                                    height: 20,
                                    fontSize: '0.65rem',
                                    borderColor: 'divider',
                                    bgcolor: 'background.paper',
                                    cursor: formUrl ? 'pointer' : 'default',
                                    '&:hover': {
                                      bgcolor: formUrl ? 'action.hover' : 'background.paper',
                                      borderColor: formUrl ? 'primary.main' : 'divider',
                                    }
                                  }}
                                />
                              );
                            })}
                          </Box>
                        </Box>
                      )}

                      {step.required_documents.length > 0 && (
                        <Box sx={{ mb: 1 }}>
                          <Typography
                            variant="caption"
                            sx={{
                              mb: 0.75,
                              fontWeight: 600,
                              color: 'text.secondary',
                              fontSize: '0.6875rem',
                              textTransform: 'uppercase',
                              letterSpacing: '0.4px'
                            }}
                          >
                            Required Documents
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {step.required_documents.map((doc, idx) => (
                              <Chip
                                key={idx}
                                label={doc}
                                size="small"
                                variant="outlined"
                                sx={{
                                  height: 20,
                                  fontSize: '0.65rem',
                                  borderColor: 'divider',
                                  bgcolor: 'background.paper',
                                  '&:hover': {
                                    bgcolor: 'action.hover'
                                  }
                                }}
                              />
                            ))}
                          </Box>
                        </Box>
                      )}

                      {Array.isArray((step.configuration as any)?.data_fields) &&
                        (step.configuration as any).data_fields.length > 0 && (
                          <Box sx={{ mb: 1 }}>
                            <Typography
                              variant="caption"
                              sx={{
                                mb: 0.75,
                                fontWeight: 600,
                                color: 'text.secondary',
                                fontSize: '0.6875rem',
                                textTransform: 'uppercase',
                                letterSpacing: '0.4px',
                                display: 'block'
                              }}
                            >
                              Required Inputs
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                              {(step.configuration as any).data_fields.map((field: any) => (
                                <TextField
                                  key={field.key}
                                  size="small"
                                  label={field.label}
                                  required={Boolean(field.required)}
                                  value={(dataFieldValues[step.id]?.[field.key]) || ''}
                                  onChange={(e) => handleDataFieldChange(step.id, field.key, e.target.value)}
                                  fullWidth
                                />
                              ))}
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => handleSaveDataFields(step.id)}
                                sx={{ fontSize: '0.75rem', px: 1.5 }}
                              >
                                Save Inputs
                              </Button>
                            </Box>
                          </Box>
                        )}

                      {/* Attachments Section */}
                      <Box sx={{ mb: 1.5 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            mb: 0.75,
                            fontWeight: 600,
                            color: 'text.secondary',
                            fontSize: '0.6875rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.4px',
                            display: 'block'
                          }}
                        >
                          Attachments
                        </Typography>
                        {step.attachments && step.attachments.length > 0 ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            {step.attachments.map((fileUrl, idx) => {
                              const fileName = fileUrl.split('/').pop() || `Attachment ${idx + 1}`;
                              return (
                                <Box
                                  key={idx}
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    p: 0.75,
                                    bgcolor: 'grey.50',
                                    borderRadius: 1,
                                    border: '1px solid',
                                    borderColor: 'divider'
                                  }}
                                >
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                                    <AttachFileIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontSize: '0.8125rem',
                                        color: 'text.primary',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        cursor: 'pointer',
                                        '&:hover': { textDecoration: 'underline' }
                                      }}
                                      onClick={() => window.open(fileUrl, '_blank')}
                                    >
                                      {fileName}
                                    </Typography>
                                  </Box>
                                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                                    <IconButton
                                      size="small"
                                      onClick={() => window.open(fileUrl, '_blank')}
                                      sx={{ p: 0.5 }}
                                    >
                                      <DownloadIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      onClick={async () => {
                                        if (window.confirm('Are you sure you want to remove this attachment?')) {
                                          try {
                                            await workflowService.removeStepAttachment(step.id, fileUrl);
                                            await fetchWorkflowStatus(); // Refresh steps
                                          } catch (error) {
                                            window.alert('Failed to remove attachment. Please try again.');
                                          }
                                        }
                                      }}
                                      sx={{ p: 0.5, color: 'error.main' }}
                                    >
                                      <DeleteIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                  </Box>
                                </Box>
                              );
                            })}
                          </Box>
                        ) : (
                          <Typography variant="body2" sx={{ fontSize: '0.8125rem', color: 'text.secondary', fontStyle: 'italic' }}>
                            No attachments yet
                          </Typography>
                        )}
                      </Box>

                      {/* Unified Action Buttons Section */}
                      <Box sx={{
                        mt: 2,
                        pt: 2,
                        borderTop: '1px solid',
                        borderColor: 'divider',
                        display: 'flex',
                        gap: 1,
                        flexWrap: 'wrap',
                        alignItems: 'center'
                      }}>
                        {/* Fill Form Button */}
                        {(step.configuration?.form_filling ||
                          step.required_forms.length > 0 ||
                          step.title.toLowerCase().includes('fill')) && (
                            <Button
                              variant="contained"
                              size="medium"
                              onClick={() => {
                                setFormFillerStep(step);
                                setShowFormFiller(true);
                              }}
                              sx={{
                                textTransform: 'none',
                                fontSize: '0.875rem',
                                fontWeight: 600,
                                px: 2.5,
                                py: 0.875,
                                borderRadius: 2,
                                boxShadow: '0 2px 8px rgba(25, 118, 210, 0.2)',
                                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                '&:hover': {
                                  boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                                  transform: 'translateY(-1px)',
                                },
                                '&:active': {
                                  transform: 'translateY(0)',
                                  boxShadow: '0 2px 8px rgba(25, 118, 210, 0.2)',
                                }
                              }}
                            >
                              Fill Form
                            </Button>
                          )}

                        {/* Upload File Button */}
                        <input
                          accept="*/*"
                          style={{ display: 'none' }}
                          id={`file-upload-${step.id}`}
                          type="file"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              try {
                                await workflowService.uploadStepAttachment(step.id, file);
                                await fetchWorkflowStatus(); // Refresh steps to show new attachment
                              } catch (error) {
                                window.alert('Failed to upload file. Please try again.');
                              }
                            }
                          }}
                        />
                        <label htmlFor={`file-upload-${step.id}`} style={{ cursor: 'pointer' }}>
                          <Button
                            component="span"
                            variant="outlined"
                            size="medium"
                            startIcon={<AttachFileIcon />}
                            sx={{
                              textTransform: 'none',
                              fontSize: '0.875rem',
                              fontWeight: 600,
                              px: 2.5,
                              py: 0.875,
                              borderRadius: 2,
                              borderWidth: 1.5,
                              borderColor: 'primary.main',
                              color: 'primary.main',
                              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                              '&:hover': {
                                borderColor: 'primary.dark',
                                backgroundColor: 'primary.50',
                                borderWidth: 1.5,
                                transform: 'translateY(-1px)',
                                boxShadow: '0 2px 8px rgba(25, 118, 210, 0.15)',
                              },
                              '&:active': {
                                transform: 'translateY(0)',
                                boxShadow: 'none',
                              }
                            }}
                          >
                            Upload File
                          </Button>
                        </label>
                      </Box>
                    </CardContent>
                    {index < steps.length - 1 && (
                      <Divider sx={{ borderColor: 'divider' }} />
                    )}
                  </Card>
                );
              })}
            </Box>
          ))}
      </Box>

      <StepCommentsDialog
        open={!!selectedStepId}
        onClose={() => setSelectedStepId(null)}
        stepId={selectedStepId || ''}
        stepTitle={selectedStepTitle}
        currentComments={selectedStepComments}
        onCommentsUpdated={async () => {
          await fetchWorkflowStatus();
        }}
      />

      {/* Add Step Dialog */}
      <Dialog
        open={showAddStepDialog}
        onClose={() => {
          setShowAddStepDialog(false);
          setNewStepData({
            title: '',
            description: '',
            category: 'general',
            estimated_days: 30,
          });
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add New Step</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Step Title"
              value={newStepData.title}
              onChange={(e) => setNewStepData({ ...newStepData, title: e.target.value })}
              required
              fullWidth
            />
            <TextField
              label="Description"
              value={newStepData.description}
              onChange={(e) => setNewStepData({ ...newStepData, description: e.target.value })}
              multiline
              rows={3}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={newStepData.category}
                label="Category"
                onChange={(e) => setNewStepData({ ...newStepData, category: e.target.value })}
              >
                <MenuItem value="general">General</MenuItem>
                <MenuItem value="documentation">Documentation</MenuItem>
                <MenuItem value="testing">Testing</MenuItem>
                <MenuItem value="submission">Submission</MenuItem>
                <MenuItem value="clinical">Clinical</MenuItem>
                <MenuItem value="quality">Quality</MenuItem>
                <MenuItem value="post_market">Post-Market</MenuItem>
              </Select>
            </FormControl>
            <TextField
              type="number"
              label="Estimated Days"
              value={newStepData.estimated_days}
              onChange={(e) => setNewStepData({ ...newStepData, estimated_days: parseInt(e.target.value) || 30 })}
              fullWidth
              InputProps={{
                endAdornment: <InputAdornment position="end">days</InputAdornment>,
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowAddStepDialog(false);
              setNewStepData({
                title: '',
                description: '',
                category: 'general',
                estimated_days: 30,
              });
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddStep}
            disabled={!newStepData.title.trim()}
          >
            Add Step
          </Button>
        </DialogActions>
      </Dialog>

      {
        formFillerStep && (
          <GuidedFormFiller
            open={showFormFiller}
            onClose={() => {
              setShowFormFiller(false);
              setFormFillerStep(null);
            }}
            step={formFillerStep!}
            workflowData={{
              name: workflowName,
              ...(workflowData ?? {}),
            }}
            onSave={async (filledFormData, filledPdfUrl) => {
              // Save filled form data to step attachments
              if (!formFillerStep) {
                return;
              }
              try {
                const currentAttachments = formFillerStep.attachments || [];
                const newAttachments = [...currentAttachments];

                if (filledPdfUrl) {
                  newAttachments.push(filledPdfUrl);
                }

                console.log('Saving filled form data:', filledFormData);

                // Refresh steps after save
                await fetchWorkflowStatus();
              } catch (error) {
                console.error('Error saving filled form:', error);
              }
            }}
          />
        )
      }
    </Box>
  );
};

